
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { pdf } from '@react-pdf/renderer';
import { useAuth } from "../contexts/AuthContext";
import Loader from "../components/Loader";
import Breadcrumbs from "../components/Breadcrumbs";
import AddRoomModal from "../components/AddRoomModal";
import AddNoteModal from "../components/AddNoteModal";
import EditNoteModal from "../components/EditNoteModal";
import AssignGuardianModal from "../components/AssignGuardianModal";
import DocumentPreviewModal from "../components/DocumentPreviewModal";
import ManageRoomsModal from "../components/ManageRoomsModal";
import ManageMetersModal from "../components/ManageMetersModal";
import propertyMetersService from "../services/propertyMetersService";
import propertiesService from "../services/propertiesService";
import buildingSetUpsService from "../services/buildingSetUpsService";
import notesService from "../services/notesService";
import roomsService from "../services/roomsService";
import propertyMonthlyInspectionsService from "../services/propertyMonthlyInspectionsService";
import initialInspectionReportsService from "../services/initialInspectionReportsService";
import propertyProposalService from "../services/propertyProposalService";
import MTVHProposalPDF from "../components/pdf/MTVHProposalPDF";
import StandardProposalPDF from "../components/pdf/StandardProposalPDF";
import MTVHMonthlyInspectionPDF from "../components/pdf/MTVHMonthlyInspectionPDF";
import StandardMonthlyInspectionPDF from "../components/pdf/StandardMonthlyInspectionPDF";
import InitialInspectionPDF from "../components/pdf/InitialInspectionPDF";
import api from "../api";
import "../assets/buttons.css";
import "../assets/panels.css";
import styles from "./PropertyShow.module.css";
import clientDashboardStyles from "../assets/clientDashboard.module.css";

const PropertyShow = () => {
  const { id } = useParams();
  const { user, hasPermission } = useAuth();

  // Laravel equivalent: CLIENT role check
  const isClientUser = user?.role === 'CLIENT';
  const [isClientView, setIsClientView] = useState(false);
  // Laravel equivalent: GUARDIAN role check - _property_overview.blade.php:2
  const isGuardianUser = user?.role === 'GUARDIAN';
  console.log('🔐 [PropertyShow] Role check - isClient:', isClientUser, 'isGuardian:', isGuardianUser, 'User role:', user?.role);

  // Date formatting function
  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // Currency formatting function - adds commas for readability
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Format date as dd/mm/yyyy (Laravel format)
  const formatDateLaravel = (dateString) => {
    if (!dateString || dateString === 'Not specified' || dateString === 'Not completed') {
      return dateString || 'Not specified';
    }
    // Some API fields (e.g. ticket.completed) already arrive pre-formatted as d/m/Y.
    // new Date() would read those as US m/d/Y and silently swap day and month, so pass them through.
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original if invalid date
      }
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return dateString; // Return original on error
    }
  };

  // Maintenance helper functions - Laravel-compatible status colors
  const getStatusColor = (status, statusId) => {
    // Use status_id if available, otherwise fall back to status name
    if (statusId !== undefined && statusId !== null) {
      // Laravel config/tickets.php statuses_advanced colors
      switch (statusId) {
        case 2: // In action
          return '#006cff';
        case 4: // Request
          return '#507d91';
        case 5: // Completed
          return '#2e8b57';
        case 6: // Declined
          return '#FF0000';
        case 7: // Contractor Completed
          return '#1adc6f';
        case 8: // Building Set Up
          return '#ff9600';
        default:
          return '#507d91'; // default gray
      }
    }
    // Fallback to status name matching
    const statusLower = (status || '').toLowerCase();
    if (statusLower.includes('completed')) {
      return '#2e8b57';
    } else if (statusLower.includes('in action')) {
      return '#006cff';
    } else if (statusLower.includes('request')) {
      return '#507d91';
    } else if (statusLower.includes('declined')) {
      return '#FF0000';
    } else if (statusLower.includes('contractor completed')) {
      return '#1adc6f';
    } else if (statusLower.includes('building set up')) {
      return '#ff9600';
    }
    return '#507d91'; // default gray
  };

  // Urgency colors - Laravel uses label-info (Low), label-warning (Medium), label-danger (High)
  const getUrgencyColor = (urgency) => {
    const urgencyLower = (urgency || '').toLowerCase();
    if (urgencyLower === 'high') {
      return '#dc3545'; // label-danger (red)
    } else if (urgencyLower === 'medium') {
      return '#ffc107'; // label-warning (orange/yellow)
    } else if (urgencyLower === 'low') {
      return '#17a2b8'; // label-info (light blue)
    }
    return '#6c757d'; // default gray
  };

  const handleDeleteTicket = async (ticketId) => {
    if (window.confirm('Are you sure you want to delete this ticket? This will also delete all related records (notes, history, expenditure items).')) {
      try {
        console.log('Deleting ticket:', ticketId);

        const response = await api.delete(`/properties/maintenance/${ticketId}/delete`);

        const result = response.data;

        if (result.success) {
          toast.success(`Ticket ${ticketId} and all related records deleted successfully`);
          console.log('✅ Delete successful:', result.data);

          // Refresh the property data to update the maintenance list
          await loadPropertyData();
        } else {
          toast.error(result.message || 'Failed to delete ticket');
          console.error('❌ Delete failed:', result);
        }
      } catch (error) {
        console.error('❌ Error deleting ticket:', error);
        toast.error('Failed to delete ticket. Please try again.');
      }
    }
  };

  const [property, setProperty] = useState(null);
  const [propertyCompany, setPropertyCompany] = useState(null);
  const [buildingManager, setBuildingManager] = useState(null);
  const [notes, setNotes] = useState([]);
  const [flaggedNotes, setFlaggedNotes] = useState([]);
  const [documents, setDocuments] = useState({});
  const [certificates, setCertificates] = useState([]);
  const [archivedCertificates, setArchivedCertificates] = useState([]);
  const [bills, setBills] = useState([]);
  const [reports, setReports] = useState([]);
  const [other, setOther] = useState([]);
  const [downloading, setDownloading] = useState({});
  const [rooms, setRooms] = useState([]);
  const [client, setClient] = useState(null);
  // Finished Building Set Up rounds, kept for audit (client, Sept 2026).
  const [bsuHistory, setBsuHistory] = useState([]);
  const [clientContact, setClientContact] = useState(null);
  const [accountManager, setAccountManager] = useState(null);

  const [savings, setSavings] = useState(null);
  const [maintenance, setMaintenance] = useState([]);
  const [maintenanceSearchTerm, setMaintenanceSearchTerm] = useState("");
  const [maintenancePageSize, setMaintenancePageSize] = useState(30);
  const [maintenanceCurrentPage, setMaintenanceCurrentPage] = useState(1);
  const [showMaintenanceColumnVisibility, setShowMaintenanceColumnVisibility] = useState(false);
  const maintenanceColumnVisibilityRef = useRef(null);
  const [maintenanceVisibleColumns, setMaintenanceVisibleColumns] = useState({
    id: true,
    status: true,
    urgency: true,
    propertyName: true,
    submitted: true,
    deadline: true,
    completed: true,
    jobType: true,
    contractorFirst: true,
    contractorLast: true,
    contractor: true,
    costOfWorks: true,
    actions: true
  });
  const [propertyReports, setPropertyReports] = useState({});
  const [liabilities, setLiabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add Room Modal state
  const [isAddRoomModalOpen, setIsAddRoomModalOpen] = useState(false);

  // Add Note Modal state
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);

  // Document Preview Modal state
  const [isDocumentPreviewModalOpen, setIsDocumentPreviewModalOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);

  // Edit Note Modal state
  const [isEditNoteModalOpen, setIsEditNoteModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  // Assign Guardian Modal state
  const [isAssignGuardianModalOpen, setIsAssignGuardianModalOpen] = useState(false);
  const [selectedRoomForAssignment, setSelectedRoomForAssignment] = useState(null);

  // Manage Rooms Modal state
  const [isManageRoomsModalOpen, setIsManageRoomsModalOpen] = useState(false);

  // Property meters register. Meters live on the property, not on an
  // inspection report, and are loaded onto each new monthly inspection.
  const [meters, setMeters] = useState([]);
  const [isManageMetersModalOpen, setIsManageMetersModalOpen] = useState(false);
  // Adding and managing are separate intentions, so they get separate entry
  // points -- matching Add room / Manage rooms above.
  const [isAddMeterModalOpen, setIsAddMeterModalOpen] = useState(false);
  // Set when the property is recorded as genuinely having no meters.
  const [noMetersConfirmed, setNoMetersConfirmed] = useState(false);

  // Close column visibility dropdown when clicking outside
  // Past Building Set Up rounds. A property that leaves BSU and comes back
  // starts a new round, and the live table only ever shows the current one, so
  // earlier rounds are read from the history endpoint.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    buildingSetUpsService.getRoundHistory(id).then((rounds) => {
      if (!cancelled) setBsuHistory(rounds || []);
    });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (maintenanceColumnVisibilityRef.current &&
          !maintenanceColumnVisibilityRef.current.contains(event.target)) {
        setShowMaintenanceColumnVisibility(false);
      }
    };

    if (showMaintenanceColumnVisibility) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMaintenanceColumnVisibility]);

  // Load the property's meters (called on mount and whenever
  // ManageMetersModal changes something).
  const refreshMeters = async () => {
    if (!id) return;
    try {
      const response = await propertyMetersService.getMetersByProperty(id);
      if (response.data?.success) {
        setMeters(response.data.data || []);
        setNoMetersConfirmed(Boolean(response.data.no_meters_confirmed));
      }
    } catch (error) {
      console.error('Error loading meters:', error);
    }
  };

  useEffect(() => {
    refreshMeters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Function to refresh rooms data (called when ManageRoomsModal makes changes)
  const refreshRooms = async () => {
    if (!id) return;
    try {
      const BASE_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${BASE_URL}/api/rooms/properties/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const result = await response.json();
      if (result.success) {
        console.log('✅ [PropertyShow] Rooms refreshed:', result.data?.length);
        setRooms(result.data || []);
      }
    } catch (error) {
      console.error('Error refreshing rooms:', error);
    }
  };

  // Property Notes table state
  const [activeFilter, setActiveFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState("date"); // Match Laravel's order: [[ 1, "desc" ]]
  const [sortDirection, setSortDirection] = useState("desc");

  useEffect(() => {
    loadPropertyData();
  }, [id]);

  const loadPropertyData = async () => {
    try {
      setLoading(true);

      // Fetch property data from API
      try {
        const response = await propertiesService.getProperty(id);
        console.log("🔍 Raw API response:", response);
        console.log("🔍 Response type:", typeof response);
        console.log("🔍 Response success:", response?.success);
        console.log("🔍 Response data:", response?.data);
        console.log("🔍 Response data property:", response?.data?.property);

        // Handle different API response structures
        let propertyData;
        console.log("🔍 Raw response structure:", {
          hasSuccess: !!response.success,
          hasData: !!response.data,
          hasProperty: !!(response.data && response.data.property),
          hasDirectProperty: !!response.property,
          responseKeys: Object.keys(response),
        });

        if (response.success && response.data && response.data.property) {
          // API returns { success: true, data: { property: {...}, propertyCompany: {...} } }
          propertyData = response.data.property;
          console.log("✅ Using live database property:", propertyData.name);

          // Set related data from API response.
          // The API returns this as `propertyCompany`; the page only ever read
          // `client`, which is why the client organisation stopped appearing
          // after the move from Codeminded. Both are accepted.
          if (response.data.propertyCompany || response.data.client) {
            setClient(response.data.propertyCompany || response.data.client);
          }
          if (response.data.buildingManager) {
            setBuildingManager(response.data.buildingManager);
          }
          if (response.data.propertyCompany) {
            setPropertyCompany(response.data.propertyCompany);
          }
          if (response.data.notes) {
            setNotes(response.data.notes);
          }
          if (response.data.flaggedNotes) {
            setFlaggedNotes(response.data.flaggedNotes);
          }
          if (response.data.documents) {
            setDocuments(response.data.documents);
            // Extract document categories from the documents object
            setCertificates(response.data.documents.certificates || []);
            setBills(response.data.documents.bills || []);
            setReports(response.data.documents.reports || []);
            setOther(response.data.documents.other || []);
          }

          // Load archived documents separately
          try {
            const archivedResponse = await propertiesService.getArchivedPropertyDocuments(id);
            if (archivedResponse.success && archivedResponse.data) {
              setArchivedCertificates(archivedResponse.data.certificates || []);
              console.log("✅ Archived certificates loaded:", archivedResponse.data.certificates);
            } else {
              setArchivedCertificates([]);
            }
          } catch (error) {
            console.log("No archived documents or error loading archived documents:", error);
            setArchivedCertificates([]);
          }
          if (response.data.rooms) {
            setRooms(response.data.rooms);
          }
        } else {
          console.error("❌ [PropertyShow] Invalid API response structure:", response);
          throw new Error("Invalid API response structure");
        }

        console.log("🔍 Extracted property data:", propertyData);

        // Ensure property_id exists (fallback to id if not)
        if (!propertyData.property_id && propertyData.id) {
          propertyData.property_id = `PROP-${propertyData.id}`;
          console.log(
            "✅ Generated property_id from id:",
            propertyData.property_id
          );
        }

        // Set live property data
        setProperty(propertyData);
        setLoading(false); // Stop loading immediately after setting property
        console.log("✅ [PropertyShow] Live property data set:", propertyData.name);
        console.log("✅ [PropertyShow] Loading set to false");

        // Log the final property data structure
        console.log("🔍 Final property data structure:", {
          id: propertyData.id,
          property_id: propertyData.property_id,
          name: propertyData.name,
          status: propertyData.status,
          status_text: propertyData.status_text,
          address_1: propertyData.address_1,
          postcode: propertyData.postcode,
        });

        // Log related data for debugging
        console.log("🔍 Related data from API:", {
          hasAccountManager: !!(response.data && response.data.accountManager),
          hasBuildingManager: !!(response.data && response.data.buildingManager),
          hasClient: !!(response.data && response.data.client),
          accountManagerData: response.data?.accountManager,
          buildingManagerData: response.data?.buildingManager,
          clientData: response.data?.client,
        });

        // Ensure status_text exists for display
        if (!propertyData.status_text && propertyData.status) {
          // Map numeric status to text if needed
          const statusMap = {
            0: "Under Offer",
            1: "Active",
            2: "Ended",
            3: "Handing Back",
            4: "Signed Proposal",
            5: "Maintenance",
            6: "Rejected Offer",
            7: "Not Full",
            8: "Full",
            9: "Pre-Offer",
            10: "Empty",
            11: "Key Holding",
            14: "Caretakers",
            999: "Automatic",
          };
          propertyData.status_text =
            statusMap[propertyData.status] || `Status ${propertyData.status}`;
        }

        // Validate that we have the essential property data
        if (!propertyData || typeof propertyData !== "object") {
          console.error("❌ Invalid property data received:", propertyData);
          setError("Invalid property data received from API");
          return;
        }

        if (!propertyData.id && !propertyData.property_id) {
          console.error("❌ Property data missing ID:", propertyData);
          setError("Property data missing required ID field");
          return;
        }

        // Additional validation for required fields
        if (!propertyData.name) {
          console.warn("⚠️ Property name missing, using fallback");
          propertyData.name = "Property Name Not Available";
        }

        if (!propertyData.address_1) {
          console.warn("⚠️ Property address missing, using fallback");
          propertyData.address_1 = "Address Not Available";
        }

        setProperty(propertyData);
        console.log("✅ Property data loaded from API:", propertyData);
        console.log(
          "🔍 Property ID from extracted data:",
          propertyData?.property_id
        );
        console.log(
          "🔍 Property Name from extracted data:",
          propertyData?.name
        );
        console.log(
          "🔍 Property Status from extracted data:",
          propertyData?.status_text
        );

        // Extract other data from the API response
        let responseData = response.data || response;

        if (responseData) {
          // Extract propertyCompany if available
          if (responseData.propertyCompany) {
            setPropertyCompany(responseData.propertyCompany);
            console.log(
              "✅ Property company loaded from API:",
              responseData.propertyCompany
            );
          }

          // Extract buildingManager if available
          if (responseData.buildingManager) {
            setBuildingManager(responseData.buildingManager);
            console.log(
              "✅ Building manager loaded from API:",
              responseData.buildingManager
            );
          } else {
            console.log("⚠️ No building manager data found in API response");
          }

          // Extract the client organisation if available. `propertyCompany`
          // is what the API actually returns; `client` is kept as a fallback.
          const clientOrganisation =
            responseData.propertyCompany || responseData.client;
          if (clientOrganisation) {
            setClient(clientOrganisation);
            console.log("✅ Client loaded from API:", clientOrganisation);
          }

          // Extract accountManager if available
          if (responseData.accountManager) {
            setAccountManager(responseData.accountManager);
            console.log(
              "✅ Account manager loaded from API:",
              responseData.accountManager
            );
          } else {
            console.log("⚠️ No account manager data found in API response");
          }

          // Extract clientContact if available
          if (responseData.clientContact) {
            setClientContact(responseData.clientContact);
            console.log(
              "✅ Client contact loaded from API:",
              responseData.clientContact
            );
          }

          // Extract notes if available
          if (responseData.notes) {
            setNotes(responseData.notes);
            console.log("✅ Notes loaded from API:", responseData.notes);
          }

          // Laravel equivalent: Extract flagged notes if available
          if (responseData.flaggedNotes) {
            setFlaggedNotes(responseData.flaggedNotes);
            console.log("✅ Flagged notes loaded from API:", responseData.flaggedNotes);
          }

          // Laravel equivalent: Check if this is client view
          if (responseData.isClientView) {
            setIsClientView(true);
            console.log("🔐 CLIENT view detected from backend");
          }

          // Extract rooms if available
          if (responseData.currentVacancies) {
            setRooms(responseData.currentVacancies);
            console.log(
              "✅ Rooms loaded from API:",
              responseData.currentVacancies
            );
          }

          // Extract documents if available
          if (responseData.propertyDocuments) {
            setDocuments(responseData.propertyDocuments);
            console.log(
              "✅ Documents loaded from API:",
              responseData.propertyDocuments
            );
          }

          // Extract maintenance if available
          if (responseData.maintenance) {
            setMaintenance(responseData.maintenance);
            console.log(
              "✅ Maintenance loaded from API:",
              responseData.maintenance
            );
          }

          // Extract reports if available
          if (responseData.propertyReports) {
            setPropertyReports(responseData.propertyReports);
            console.log(
              "✅ Reports loaded from API:",
              responseData.propertyReports
            );
          }

          // Extract liabilities if available
          if (responseData.liabilities) {
            setLiabilities(responseData.liabilities);
            console.log(
              "✅ Liabilities loaded from API:",
              responseData.liabilities
            );
          }

          // Extract accommodation if available
          if (responseData.accommodation) {
            // This might need to be mapped to rooms or handled separately
            console.log(
              "✅ Accommodation loaded from API:",
              responseData.accommodation
            );
          }

          // Extract financial summary if available
          if (responseData.financialSummary) {
            console.log(
              "✅ Financial summary loaded from API:",
              responseData.financialSummary
            );
            // Map the financial summary data to the savings state
            const financialData = responseData.financialSummary;
            setSavings({
              savings_made: financialData.savings?.replace(/[+£,]/g, '') || "0.00",
              vip_amount: financialData.profit?.replace(/[+£,]/g, '') || "0.00",
              savings_data_incomplete: financialData.savings_data_incomplete || false
            });
          }

          // Extract property photos if available
          if (responseData.propertyPhotos) {
            // This might need to be handled separately
            console.log(
              "✅ Property photos loaded from API:",
              responseData.propertyPhotos
            );
          }
        }
      } catch (error) {
        console.error("❌ [PropertyShow] Error fetching property from API:", error);

        // Laravel equivalent: Handle access denied errors
        if (error.response && error.response.status === 403) {
          const errorData = error.response.data;
          if (errorData.error === "ACCESS_DENIED") {
            console.log("🚫 [FRONTEND] Access denied by backend:", errorData.message);
            toast.error(errorData.message || "You don't have permission to view this property");
            window.open("/dashboard", "_blank");
            return;
          }
        }

        // API failed - show error and stop loading
        console.error("❌ [PropertyShow] API failed, no fallback data");
        setError("Failed to load property data. Please try again later.");
        setLoading(false);
        return;
      }

      // Load property notes from API
      try {
        const notesResponse = await notesService.getPropertyNotes(id);
        if (notesResponse.success) {
          setNotes(notesResponse.data);
          console.log("✅ Property notes loaded from API:", notesResponse.data);
        } else {
          console.error(
            "Failed to load property notes:",
            notesResponse.message
          );
          setNotes([]);
        }
      } catch (error) {
        console.error("Error fetching property notes:", error);
        setNotes([]);
        toast.warning("Failed to load property notes");
      }

      // NOTE: Rooms are already loaded from currentVacancies above (line 409-415)
      // The getRoomsByPropertyId endpoint returns different data structure with rent as string
      // which would overwrite the correct currentVacancies data
      // So we skip this call to preserve the correct currentVacancies data structure
      // If currentVacancies was not loaded, rooms will remain empty (which is correct)

      // Flagged notes are now loaded from backend API response via responseData.flaggedNotes

      // Documents are loaded from API response above

      // Certificates are loaded from API response above

      // Client and client contact data are loaded from API response above

      // Building manager will be loaded from API if available

      // Savings data is loaded from API response above

      // Maintenance data is loaded from API response above

      // Reports data is loaded from API response above

      // Liabilities are now handled from API response above
    } catch (err) {
      console.error("Error loading property data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPropertyDetails = async () => {
    try {
      await propertiesService.exportPropertyDetails(id);
      toast.success("Property details exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export property details");
    }
  };

  const handleExportGuardiansDetails = async () => {
    try {
      await propertiesService.exportPlacedGuardiansCSV(id);
      toast.success("Placed guardians details exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export placed guardians details");
    }
  };

  const handleExportGuardiansPDF = async () => {
    try {
      await propertiesService.exportPlacedGuardiansPDF(id);
      toast.success("Placed guardians PDF exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export placed guardians PDF");
    }
  };

  // Matches Laravel's Property::getFullAddressForCopyAttribute()
  const buildFullAddress = () => {
    if (!property) return "";

    let address = property.address_1 || "";
    if (property.address_2) {
      address = address + " " + property.address_2;
    }
    if (property.address_3) {
      address = address + " " + property.address_3;
    }

    return (
      address.trim() +
      ", " +
      (property.town || "").trim() +
      ", " +
      (property.postcode || "").trim()
    );
  };

  const copyAddress = async () => {
    const fullAddress = buildFullAddress();

    if (!fullAddress.replace(/,/g, "").trim()) {
      toast.error("Address not available");
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(fullAddress);
      } else {
        // Fallback for non-secure contexts (matches Laravel's execCommand approach)
        const aux = document.createElement("input");
        aux.setAttribute("value", fullAddress);
        document.body.appendChild(aux);
        aux.select();
        document.execCommand("copy");
        document.body.removeChild(aux);
      }
      toast.success("Address copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy address");
    }
  };

  // Property Notes table functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc"); // Default to desc for date sorting like Laravel
    }
  };

  // Helper function to get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return "glyphicon-sort"; // Neutral sort icon
    }
    return sortDirection === "asc" ? "glyphicon-sort-by-attributes" : "glyphicon-sort-by-attributes-alt";
  };

  const handleEditNote = async (noteId) => {
    try {
      const noteToEdit = notes.find((note) => note.id === noteId);
      if (noteToEdit) {
        // Open edit modal with note data
        setEditingNote(noteToEdit);
        setIsEditNoteModalOpen(true);
      } else {
        toast.error("Note not found");
      }
    } catch (error) {
      console.error("Error preparing to edit note:", error);
      toast.error("Failed to prepare note for editing");
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      // Confirm deletion
      if (window.confirm("Are you sure you want to delete this note?")) {
        const response = await notesService.deleteNote(noteId);

        if (response.success) {
          // Remove the note from the local state
          setNotes(notes.filter((note) => note.id !== noteId));
          toast.success("Note deleted successfully!");
        } else {
          toast.error(response.message || "Failed to delete note");
        }
      }
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Failed to delete note");
    }
  };

  const handleAddNote = () => {
    setIsAddNoteModalOpen(true);
  };

  // Document Preview Modal handler
  const handleShowDocumentPreview = (document) => {
    setPreviewDocument(document);
    setIsDocumentPreviewModalOpen(true);
  };

  // Document action handlers
  const handleShowDocument = async (documentId, documentName) => {
    try {
      console.log(`📄 Showing document: ${documentName} (ID: ${documentId})`);
      const response = await propertiesService.showPropertyDocument(id, documentId);

      if (response.success) {
        // For now, just show a toast with document details
        // In a real implementation, this could open a modal or navigate to a detail view
        toast.info(`Document details: ${documentName}`);
        console.log("Document details:", response.data);
      } else {
        toast.error(response.message || "Failed to show document");
      }
    } catch (error) {
      console.error("Error showing document:", error);
      toast.error("Failed to show document");
    }
  };

  const handleEditDocument = (documentId, documentName) => {
    console.log(`✏️ Navigating to edit document: ${documentName} (ID: ${documentId})`);
    window.open(`/properties/${id}/documents/${documentId}/edit`, '_blank');
  };

  const handleDownloadDocument = async (documentId, documentName, downloadUrl = null) => {
    console.log('🔍 handleDownloadDocument CALLED:', { documentId, documentName, downloadUrl, propertyId: id });
    try {
      // Laravel equivalent: CLIENT users can download documents visible to them
      // No permission check needed - if they can see it, they can download it
      console.log(`⬇️ Downloading document: ${documentName} (ID: ${documentId})`);

      // If downloadUrl is provided (for media documents like bills/reports), use it directly
      // Otherwise use the property documents service (for certificates from documents table)
      const response = downloadUrl
        ? await propertiesService.downloadDocumentByUrl(downloadUrl, documentName)
        : await propertiesService.downloadPropertyDocument(id, documentId);

      if (response.success) {
        toast.success(`Document downloaded: ${documentName}`);
      } else {
        toast.error(response.message || "Failed to download document");
      }
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Failed to download document");
    }
  };

  const handleRemoveDocument = async (documentId, documentName) => {
    try {
      // Laravel equivalent: Check delete permissions (admin only)
      if (!hasPermission('documents.delete') || isClientUser) {
        toast.error("You don't have permission to delete documents");
        return;
      }

      // Confirm deletion
      if (window.confirm(`Are you sure you want to remove the document "${documentName}"? This action cannot be undone.`)) {
        console.log(`🗑️ Removing document: ${documentName} (ID: ${documentId})`);
        const response = await propertiesService.deletePropertyDocument(id, documentId);

        if (response.success) {
          toast.success(`Document removed: ${documentName}`);
          // Reload the property data to update the document list
          loadPropertyData();
        } else {
          toast.error(response.message || "Failed to remove document");
        }
      }
    } catch (error) {
      console.error("Error removing document:", error);
      toast.error("Failed to remove document");
    }
  };

  const handleArchiveDocument = async (documentId, documentName) => {
    try {
      // Laravel equivalent: Check archive permissions
      if (!hasPermission('documents.archive') || isClientUser) {
        toast.error("You don't have permission to archive documents");
        return;
      }

      console.log(`📦 Archiving document: ${documentName} (ID: ${documentId})`);

      // Show confirmation dialog
      const confirmed = window.confirm(
        `Are you sure you want to archive "${documentName}"? This document will be hidden from the certificates section.`
      );

      if (!confirmed) {
        return;
      }

      // Call the archive API
      const response = await api.put(`/properties/${id}/documents/${documentId}/archive`);

      const result = response.data;

      if (result.success) {
        toast.success(`Document "${documentName}" archived successfully`);

        // Refresh the property data to update the certificates list
        await loadPropertyData();
      } else {
        toast.error(result.message || "Failed to archive document");
      }
    } catch (error) {
      console.error("Error archiving document:", error);
      toast.error("Failed to archive document");
    }
  };

  const handleActivateDocument = async (documentId, documentName) => {
    try {
      console.log(`🔄 Activating document: ${documentName} (ID: ${documentId})`);

      // Show confirmation dialog
      const confirmed = window.confirm(
        `Are you sure you want to activate "${documentName}"? This document will be shown in the certificates section.`
      );

      if (!confirmed) {
        return;
      }

      // Call the activate API
      const response = await api.put(`/properties/${id}/documents/${documentId}/activate`);

      const result = response.data;

      if (result.success) {
        toast.success(`Document "${documentName}" activated successfully`);

        // Refresh the property data to update the certificates list
        await loadPropertyData();
      } else {
        toast.error(result.message || "Failed to activate document");
      }
    } catch (error) {
      console.error("Error activating document:", error);
      toast.error("Failed to activate document");
    }
  };

  // Handle static document downloads (onboarding and handback process)
  const handleStaticDocumentDownload = (documentType, documentName) => {
    try {
      setDownloading((prev) => ({ ...prev, [documentType]: true }));

      console.log(`📥 Downloading static PDF: ${documentName} (${documentType})`);

      // Static document file mappings
      const documentFiles = {
        onboarding_process: '/documents/guides/guardian_onboarding.pdf',
        handback_process: '/documents/guardian-handbook/guardian_handbook.pdf',
      };

      const fileUrl = documentFiles[documentType];

      if (!fileUrl) {
        toast.error(`Document file not found: ${documentName}`);
        console.error(`No file mapping found for document type: ${documentType}`);
        setDownloading((prev) => ({ ...prev, [documentType]: false }));
        return;
      }

      // Directly download the PDF without any API calls
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileUrl.split('/').pop(); // This triggers download
      link.rel = 'noopener noreferrer';

      // Trigger the link
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`📥 ${documentName} download started!`);
      console.log(`✅ Successfully started download: ${documentName} from ${fileUrl}`);

    } catch (error) {
      console.error('❌ Error in static document download:', error);
      toast.error('Failed to download document. Please try again.');
    } finally {
      // Reset downloading state after a short delay
      setTimeout(() => {
        setDownloading((prev) => ({ ...prev, [documentType]: false }));
      }, 500);
    }
  };

  const handleNoteUpdated = async () => {
    try {
      // Refresh notes from API after update
      const response = await notesService.getPropertyNotes(id);
      if (response.success) {
        setNotes(response.data || []);
      }
    } catch (error) {
      console.error("Error refreshing notes:", error);
    }
  };

  // Handle delete Monthly Inspection Report
  const handleDeleteMonthlyInspectionReport = async (reportId) => {
    if (window.confirm('Are you sure you want to remove this Monthly Inspection Report?')) {
      try {
        await propertyMonthlyInspectionsService.deleteMonthlyInspectionReport(reportId);
        toast.success('Monthly Inspection Report deleted successfully!');
        // Refresh the reports data
        loadReports();
      } catch (error) {
        console.error('Error deleting Monthly Inspection Report:', error);
        toast.error('Failed to delete Monthly Inspection Report');
      }
    }
  };

  // Handle delete Initial Inspection Report
  const handleDeleteInitialInspectionReport = async (reportId) => {
    if (window.confirm('Are you sure you want to remove this Initial Inspection Report?')) {
      try {
        await initialInspectionReportsService.deleteInitialInspectionReport(reportId);
        toast.success('Initial Inspection Report deleted successfully!');
        // Refresh the reports data
        loadReports();
      } catch (error) {
        console.error('Error deleting Initial Inspection Report:', error);
        toast.error('Failed to delete Initial Inspection Report');
      }
    }
  };

  // Handle delete Property Proposal
  const handleDeletePropertyProposal = async (reportId) => {
    if (window.confirm('Are you sure you want to remove this Property Proposal?')) {
      try {
        await propertyProposalService.deletePropertyProposal(id, reportId);
        toast.success('Property Proposal deleted successfully!');
        // Refresh the reports data
        loadReports();
      } catch (error) {
        console.error('Error deleting Property Proposal:', error);
        toast.error('Failed to delete Property Proposal');
      }
    }
  };

  // Handle PDF download for Monthly Inspection Reports (Frontend Generation)
  const handleDownloadMonthlyInspectionPDF = async (reportId) => {
    try {
      toast.info('Generating PDF...');

      // Fetch the monthly inspection report data
      const response = await propertyMonthlyInspectionsService.getMonthlyInspectionReport(reportId);
      const reportData = response.data?.data || response.data;
      console.log('Monthly Inspection Report Data:', reportData);
      console.log('Property object:', property);
      console.log('Client object:', client);

      // Parse JSON fields if they're strings
      const parseJsonField = (field) => {
        if (!field) return {};
        if (typeof field === 'object') return field;
        try {
          return JSON.parse(field);
        } catch (e) {
          console.error('Error parsing JSON field:', e);
          return {};
        }
      };

      // Parse meter readings from the combined structure
      let readingFields = [];
      let readingFieldsNames = [];
      let readingFieldsSerials = [];
      
      if (reportData.reading_fields) {
        const parsed = Array.isArray(reportData.reading_fields) 
          ? reportData.reading_fields 
          : (typeof reportData.reading_fields === 'string' ? JSON.parse(reportData.reading_fields) : []);
        
        if (Array.isArray(parsed) && parsed.length > 0) {
          // Check if it's Laravel's structure: [[name, reading, serial], ...]
          if (Array.isArray(parsed[0])) {
            readingFieldsNames = parsed.map(item => item[0] || '');
            readingFields = parsed.map(item => item[1] || '');
            readingFieldsSerials = parsed.map(item => item[2] || '');
          } else if (parsed[0].reading !== undefined) {
            // New structure: [{reading, name, serial}, ...]
            readingFields = parsed.map(item => item.reading || '');
            readingFieldsNames = parsed.map(item => item.name || '');
            readingFieldsSerials = parsed.map(item => item.serial || '');
          } else {
            // Simple array of readings
            readingFields = parsed;
          }
        }
      }

      // Transform the data for the PDF component
      const pdfData = {
        // Report info
        month: reportData.month,
        year: reportData.year,
        inspection_date: reportData.inspection_date,
        
        // Property info
        address_1: reportData.address_1 || property?.address_1 || property?.property_address_1 || '',
        address_2: reportData.address_2 || property?.address_2 || '',
        town: reportData.town || property?.town || '',
        postcode: reportData.postcode || property?.postcode || property?.property_postcode || '',
        property_id_name: reportData.property_id_name || property?.property_id || '',
        key_number: reportData.key_number || reportData.property_id_name || property?.property_id || '',
        
        // Client info
        client: reportData.client || client?.name || property?.client_name || '',
        client_contact: reportData.client_contact || property?.client_contact || '',
        guardians_number: reportData.guardians_number || property?.guardians_number || '',
        
        // Account manager
        account_manager: reportData.account_manager || property?.account_manager || '',
        account_manager_email: reportData.account_manager_email || property?.account_manager_email || '',
        
        // Meter readings.
        // Legacy (Sep 2026 and earlier): the parsed triples below.
        // Oct 2026+: rows from the property meter register, passed straight
        // through -- the PDF templates pick which to use via usesNewMeters().
        reading_fields: readingFields,
        reading_fields_names: readingFieldsNames,
        reading_fields_serials: readingFieldsSerials,
        meter_readings: reportData.meter_readings || [],
        
        // Photos (with presigned URLs)
        image_1_url: reportData.image_1_url || null,
        image_2_url: reportData.image_2_url || null,
        image_3_url: reportData.image_3_url || null,
        image_4_url: reportData.image_4_url || null,
        image_5_url: reportData.image_5_url || null,
        image_6_url: reportData.image_6_url || null,
        image_7_url: reportData.image_7_url || null,
        image_8_url: reportData.image_8_url || null,
        image_9_url: reportData.image_9_url || null,
        image_10_url: reportData.image_10_url || null,
        image_11_url: reportData.image_11_url || null,
        image_12_url: reportData.image_12_url || null,
        image_13_url: reportData.image_13_url || null,
        image_14_url: reportData.image_14_url || null,

        // MTVH additional photos (a/b variants)
        image_1_a_url: reportData.image_1_a_url || null,
        image_1_b_url: reportData.image_1_b_url || null,
        image_2_a_url: reportData.image_2_a_url || null,
        image_2_b_url: reportData.image_2_b_url || null,
        image_3_a_url: reportData.image_3_a_url || null,
        image_3_b_url: reportData.image_3_b_url || null,
        image_4_a_url: reportData.image_4_a_url || null,
        image_4_b_url: reportData.image_4_b_url || null,

        // Photo names
        image_1_name: reportData.image_1_name || 'Front',
        image_2_name: reportData.image_2_name || 'Bedroom',
        image_3_name: reportData.image_3_name || 'Kitchen/Bathroom',
        image_4_name: reportData.image_4_name || 'Communal Area',
        // Meter photo names (image_5..image_14)
        image_5_name: reportData.image_5_name || 'Gas Meter 1',
        image_6_name: reportData.image_6_name || 'Gas Meter 2',
        image_7_name: reportData.image_7_name || 'Electric Meter',
        image_8_name: reportData.image_8_name || 'Water Meter',
        image_9_name: reportData.image_9_name || '',
        image_10_name: reportData.image_10_name || '',
        image_11_name: reportData.image_11_name || '',
        image_12_name: reportData.image_12_name || '',
        image_13_name: reportData.image_13_name || '',
        image_14_name: reportData.image_14_name || '',

        // Checklist sections (parsed from JSON)
        external: parseJsonField(reportData.external),
        kitchen: parseJsonField(reportData.kitchen),
        living_space: parseJsonField(reportData.living_space),
        bathroom: parseJsonField(reportData.bathroom),
        other: parseJsonField(reportData.other),
        health_and_safety: parseJsonField(reportData.health_and_safety),
        location: parseJsonField(reportData.location),
        
        // Brief overview
        brief_overview: reportData.brief_overview || '',
        
        // Signature
        e_sign: reportData.e_sign || null,
        
        // Head guardian
        head_guardian: reportData.head_guardian || '',
      };

      console.log('Transformed PDF Data:', pdfData);

      // Check if this is a Metropolitan Housing (MTVH) property
      // Check multiple sources to ensure correct detection
      const isMetropolitanHousing =
        property?.is_metropolitan_housing ||
        property?.isMetropolitanHousing ||
        reportData?.is_metropolitan_housing ||
        // Check client name from various sources
        (reportData?.client && reportData.client.toLowerCase().includes('metropolitan')) ||
        (client?.name && client.name.toLowerCase().includes('metropolitan')) ||
        (property?.client_name && property.client_name.toLowerCase().includes('metropolitan')) ||
        // Check if client name contains "mtvh"
        (reportData?.client && reportData.client.toLowerCase().includes('mtvh')) ||
        (client?.name && client.name.toLowerCase().includes('mtvh')) ||
        (property?.client_name && property.client_name.toLowerCase().includes('mtvh')) ||
        false;

      console.log('Is Metropolitan Housing:', isMetropolitanHousing);
      console.log('Client from reportData:', reportData?.client);
      console.log('Client name:', client?.name);
      console.log('Property client_name:', property?.client_name);

      // Generate the PDF using the appropriate PDF component
      let pdfDocument;
      let fileName;
      
      if (isMetropolitanHousing) {
        // Use MTVH Monthly Inspection PDF for Metropolitan Housing
        pdfDocument = <MTVHMonthlyInspectionPDF data={pdfData} />;
        fileName = `Monthly_Inspection_Report_MTVH_${reportId}.pdf`;
      } else {
        // Use Standard Monthly Inspection PDF for other clients
        pdfDocument = <StandardMonthlyInspectionPDF data={pdfData} />;
        fileName = `Monthly_Inspection_Report_${reportId}.pdf`;
      }
      
      console.log('Generating PDF blob...');
      const blob = await pdf(pdfDocument).toBlob();
      console.log('PDF blob generated successfully');

      // Download the PDF
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to download PDF';
      toast.error(`PDF Download Error: ${errorMessage}`);
    }
  };

  // Handle PDF download for Initial Inspection Reports (Frontend Generation)
  const handleDownloadInitialInspectionPDF = async (reportId) => {
    try {
      toast.info('Generating PDF...');

      // Fetch the initial inspection report data
      const response = await initialInspectionReportsService.getInitialInspectionReport(reportId);
      const reportData = response.data || response;
      console.log('Initial Inspection Report Data:', reportData);

      // Parse rooms if it's a JSON string
      let rooms = [];
      console.log('Raw rooms data:', reportData.rooms, 'Type:', typeof reportData.rooms);
      if (reportData.rooms) {
        if (typeof reportData.rooms === 'string') {
          try {
            rooms = JSON.parse(reportData.rooms);
            console.log('Parsed rooms from string:', rooms);
          } catch (e) {
            console.error('Error parsing rooms:', e);
            rooms = [];
          }
        } else if (Array.isArray(reportData.rooms)) {
          rooms = reportData.rooms;
          console.log('Rooms is already an array:', rooms);
        } else if (typeof reportData.rooms === 'object') {
          // Handle object with numeric keys
          rooms = Object.values(reportData.rooms);
          console.log('Converted rooms from object:', rooms);
        }
      }
      console.log('Final rooms array:', rooms, 'Length:', rooms.length);

      // Prepare data for PDF
      const pdfData = {
        ...reportData,
        rooms,
        // Map image URLs if they exist
        image_gas_meters_url: reportData.image_gas_meters_url || reportData.image_gas_meters,
        image_electric_meters_url: reportData.image_electric_meters_url || reportData.image_electric_meters,
        image_water_meters_url: reportData.image_water_meters_url || reportData.image_water_meters,
        image_fuse_boards_url: reportData.image_fuse_boards_url || reportData.image_fuse_boards,
      };

      console.log('PDF Data prepared:', pdfData);

      // Generate PDF
      const pdfDocument = <InitialInspectionPDF data={pdfData} />;
      const blob = await pdf(pdfDocument).toBlob();
      
      // Download the PDF
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Initial_Inspection_Report_${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  // Handle PDF download for Property Proposals (Frontend Generation)
  const handleDownloadPropertyProposalPDF = async (reportId) => {
    try {
      toast.info('Generating PDF...');

      // Fetch the actual property proposal data from the backend
      // Note: propertyProposalService.get() already returns response.data
      const apiData = await propertyProposalService.get(id, reportId);
      console.log('API Data:', apiData);

      // Extract the report data and map it to the format expected by PDF components
      const reportData = apiData.report || apiData;
      console.log('Report Data:', reportData);

      // Determine if this is a Metropolitan Housing property
      // Check multiple sources to ensure correct detection
      const isMetropolitanHousing =
        reportData.is_metropolitan_housing ||
        property?.is_metropolitan_housing ||
        property?.isMetropolitanHousing ||
        apiData.is_metropolitan_housing ||
        apiData.property?.is_metropolitan_housing ||
        // Fallback: check client name
        (reportData.client && reportData.client.toLowerCase().includes('metropolitan')) ||
        (client?.name && client.name.toLowerCase().includes('metropolitan')) ||
        false;

      console.log('Is Metropolitan Housing:', isMetropolitanHousing);
      console.log('Client name:', reportData.client || client?.name);
      console.log('Property object:', property);

      // Transform the data to match PDF component expectations
      const pdfData = {
        // Client information
        client: reportData.client || client?.name || '',
        client_contact_name: reportData.client_contact_name || clientContact?.full_name || '',
        client_contact_title: reportData.client_contact_title || clientContact?.job_title || '',
        address: reportData.address || client?.address || '',
        client_address: reportData.client_address || reportData.address || client?.address || '',

        // Property information - use ?? to preserve empty strings from proposal
        property_address_1: reportData.property_address_1 ?? property?.address_1 ?? 'N/A',
        property_address_2: reportData.property_address_2 ?? property?.address_2 ?? '',
        property_address_3: reportData.property_address_3 ?? property?.address_3 ?? '',
        property_postcode: reportData.property_postcode ?? property?.postcode ?? 'N/A',
        property_town: reportData.property_town ?? property?.town ?? '',

        // Proposal details
        council_tax_liability: reportData.council_tax_liability || 'N/A',
        full_security: reportData.full_security || 'N/A',
        proposed_works: reportData.proposed_works ?
          (typeof reportData.proposed_works === 'string' ?
            JSON.parse(reportData.proposed_works) :
            reportData.proposed_works) : [],
        unusual_matters: reportData.unusual_matters || 'None',

        // Account manager
        account_manager_name: reportData.account_manager_name || 'N/A',
        account_manager_email: reportData.account_manager_email || 'N/A',
        account_manager_phone: reportData.account_manager_phone || 'N/A',

        // MTVH specific fields
        initial_fit_out_works: reportData.initial_fit_out_works || '',
        sia_security_guards: reportData.sia_security_guards || 0,
        sia_security_guards_description: reportData.sia_security_guards_description || '',
        fit_out_timescales: reportData.fit_out_timescales || '',
        start_up_fees: reportData.start_up_fees || '',
        min_contract_period: reportData.min_contract_period || '',
        min_contract_period_word: reportData.min_contract_period_word || '',

        // Liability fields - these drive the {{placeholders}} inside the clause text
        asbestos: reportData.asbestos || '',
        asbestos_free_of_charge: reportData.asbestos_free_of_charge || '',
        legionella: reportData.legionella || '',
        utilities_maintenance: reportData.utilities_maintenance || '',
        utilities_gas_electric: reportData.utilities_gas_electric || '',
        utilities_water: reportData.utilities_water || '',
        council_tax: reportData.council_tax || '',
        business_rates: reportData.business_rates || '',
        waste_arrange: reportData.waste_arrange || '',
        waste_costs: reportData.waste_costs || '',
        managing_the_property: reportData.managing_the_property || '',

        // NPP Key Information fields
        npp_minimum_contract_period: reportData.npp_minimum_contract_period || '',
        npp_notice_period: reportData.npp_notice_period || '',
        npp_revenue_share: reportData.npp_revenue_share || '',

        // NPP General Liability fields
        npp_myglobe_subscription_rm: reportData.npp_myglobe_subscription_rm || '',
        npp_myglobe_subscription_rp: reportData.npp_myglobe_subscription_rp || '',
        npp_myglobe_subscription_cow: reportData.npp_myglobe_subscription_cow || '',
        npp_management_fee_rm: reportData.npp_management_fee_rm || '',
        npp_management_fee_rp: reportData.npp_management_fee_rp || '',
        npp_management_fee_cow: reportData.npp_management_fee_cow || '',
        npp_building_insurance_rm: reportData.npp_building_insurance_rm || '',
        npp_building_insurancee_rp: reportData.npp_building_insurancee_rp || '',
        npp_building_insurance_cow: reportData.npp_building_insurance_cow || '',
        npp_public_liability_insurance_rm: reportData.npp_public_liability_insurance_rm || '',
        npp_public_liability_insurance_rp: reportData.npp_public_liability_insurance_rp || '',
        npp_public_liability_insurance_cow: reportData.npp_public_liability_insurance_cow || '',
        npp_sia_security_rm: reportData.npp_sia_security_rm || '',
        npp_sia_security_rp: reportData.npp_sia_security_rp || '',
        npp_sia_security_cow: reportData.npp_sia_security_cow || '',
        npp_catetaker_rm: reportData.npp_catetaker_rm || '',
        npp_catetaker_rp: reportData.npp_catetaker_rp || '',
        npp_catetaker_cow: reportData.npp_catetaker_cow || '',
        npp_access_egress_rm: reportData.npp_access_egress_rm || '',
        npp_access_egress_rp: reportData.npp_access_egress_rp || '',
        npp_access_egress_cow: reportData.npp_access_egress_cow || '',
        npp_monthly_inspections_rm: reportData.npp_monthly_inspections_rm || '',
        npp_monthly_inspections_rp: reportData.npp_monthly_inspections_rp || '',
        npp_monthly_inspections_cow: reportData.npp_monthly_inspections_cow || '',
        npp_weekly_inspections_rm: reportData.npp_weekly_inspections_rm || '',
        npp_weekly_inspections_rp: reportData.npp_weekly_inspections_rp || '',
        npp_weekly_inspections_cow: reportData.npp_weekly_inspections_cow || '',
        npp_hmo_liscense_rm: reportData.npp_hmo_liscense_rm || '',
        npp_hmo_liscense_rp: reportData.npp_hmo_liscense_rp || '',
        npp_hmo_liscense_cow: reportData.npp_hmo_liscense_cow || '',
        npp_selective_license_rm: reportData.npp_selective_license_rm || '',
        npp_selective_license_rp: reportData.npp_selective_license_rp || '',
        npp_selective_license_cow: reportData.npp_selective_license_cow || '',
        npp_floor_plans_rm: reportData.npp_floor_plans_rm || '',
        npp_floor_plans_rp: reportData.npp_floor_plans_rp || '',
        npp_floor_plans_cow: reportData.npp_floor_plans_cow || '',
        npp_voa_application_rm: reportData.npp_voa_application_rm || '',
        npp_voa_application_rp: reportData.npp_voa_application_rp || '',
        npp_voa_application_cow: reportData.npp_voa_application_cow || '',

        // NPP Utility Liability fields
        npp_gas_rm: reportData.npp_gas_rm || '',
        npp_gas_rp: reportData.npp_gas_rp || '',
        npp_gas_csan: reportData.npp_gas_csan || '',
        npp_electricity_rm: reportData.npp_electricity_rm || '',
        npp_electricity_rp: reportData.npp_electricity_rp || '',
        npp_electricity_csan: reportData.npp_electricity_csan || '',
        npp_water_rm: reportData.npp_water_rm || '',
        npp_water_rp: reportData.npp_water_rp || '',
        npp_water_csan: reportData.npp_water_csan || '',
        npp_business_rates_rm: reportData.npp_business_rates_rm || '',
        npp_business_rates_rp: reportData.npp_business_rates_rp || '',
        npp_business_rates_csan: reportData.npp_business_rates_csan || '',
        npp_council_tax_rm: reportData.npp_council_tax_rm || '',
        npp_council_tax_rp: reportData.npp_council_tax_rp || '',
        npp_council_tax_csan: reportData.npp_council_tax_csan || '',
        npp_bins_rm: reportData.npp_bins_rm || '',
        npp_bins_rp: reportData.npp_bins_rp || '',
        npp_bins_csan: reportData.npp_bins_csan || '',

        // NPP Health & Safety Liability fields
        npp_gas_safe_rm: reportData.npp_gas_safe_rm || '',
        npp_gas_safe_rp: reportData.npp_gas_safe_rp || '',
        npp_gas_safe_dr: reportData.npp_gas_safe_dr || '',
        npp_gas_safe_cow: reportData.npp_gas_safe_cow || '',
        npp_nic_rm: reportData.npp_nic_rm || '',
        npp_nic_rp: reportData.npp_nic_rp || '',
        npp_nic_dr: reportData.npp_nic_dr || '',
        npp_nic_cow: reportData.npp_nic_cow || '',
        npp_legionella_rm: reportData.npp_legionella_rm || '',
        npp_legionella_rp: reportData.npp_legionella_rp || '',
        npp_legionella_dr: reportData.npp_legionella_dr || '',
        npp_legionella_cow: reportData.npp_legionella_cow || '',
        npp_asbestos_rm: reportData.npp_asbestos_rm || '',
        npp_asbestos_rp: reportData.npp_asbestos_rp || '',
        npp_asbestos_dr: reportData.npp_asbestos_dr || '',
        npp_asbestos_cow: reportData.npp_asbestos_cow || '',
        npp_fire_risk_rm: reportData.npp_fire_risk_rm || '',
        npp_fire_risk_rp: reportData.npp_fire_risk_rp || '',
        npp_fire_risk_dr: reportData.npp_fire_risk_dr || '',
        npp_fire_risk_cow: reportData.npp_fire_risk_cow || '',
        npp_fire_alarm_rm: reportData.npp_fire_alarm_rm || '',
        npp_fire_alarm_rp: reportData.npp_fire_alarm_rp || '',
        npp_fire_alarm_dr: reportData.npp_fire_alarm_dr || '',
        npp_fire_alarm_cow: reportData.npp_fire_alarm_cow || '',
        npp_emergency_lighting_rm: reportData.npp_emergency_lighting_rm || '',
        npp_emergency_lighting_rp: reportData.npp_emergency_lighting_rp || '',
        npp_emergency_lighting_dr: reportData.npp_emergency_lighting_dr || '',
        npp_emergency_lighting_cow: reportData.npp_emergency_lighting_cow || '',
        npp_fire_safety_rm: reportData.npp_fire_safety_rm || '',
        npp_fire_safety_rp: reportData.npp_fire_safety_rp || '',
        npp_fire_safety_dr: reportData.npp_fire_safety_dr || '',
        npp_fire_safety_cow: reportData.npp_fire_safety_cow || '',
        npp_fire_extinguisher_rm: reportData.npp_fire_extinguisher_rm || '',
        npp_fire_extinguisher_rp: reportData.npp_fire_extinguisher_rp || '',
        npp_fire_extinguisher_dr: reportData.npp_fire_extinguisher_dr || '',
        npp_fire_extinguisher_cow: reportData.npp_fire_extinguisher_cow || '',

        // NPP Maintenance Liability fields
        npp_initial_fitout_rm: reportData.npp_initial_fitout_rm || '',
        npp_initial_fitout_rp: reportData.npp_initial_fitout_rp || '',
        npp_initial_fitout_cow: reportData.npp_initial_fitout_cow || '',
        npp_locks_keys_rm: reportData.npp_locks_keys_rm || '',
        npp_locks_keys_rp: reportData.npp_locks_keys_rp || '',
        npp_locks_keys_cow: reportData.npp_locks_keys_cow || '',
        npp_ongoing_internal_maintenance_rm: reportData.npp_ongoing_internal_maintenance_rm || '',
        npp_ongoing_internal_maintenance_rp: reportData.npp_ongoing_internal_maintenance_rp || '',
        npp_ongoing_internal_maintenance_cow: reportData.npp_ongoing_internal_maintenance_cow || '',
        npp_boiler_repair_rm: reportData.npp_boiler_repair_rm || '',
        npp_boiler_repair_rp: reportData.npp_boiler_repair_rp || '',
        npp_boiler_repair_cow: reportData.npp_boiler_repair_cow || '',
        npp_ongoing_external_maintenance_rm: reportData.npp_ongoing_external_maintenance_rm || '',
        npp_ongoing_external_maintenance_rp: reportData.npp_ongoing_external_maintenance_rp || '',
        npp_ongoing_external_maintenance_cow: reportData.npp_ongoing_external_maintenance_cow || '',
        npp_cleaning_areas_rm: reportData.npp_cleaning_areas_rm || '',
        npp_cleaning_areas_rp: reportData.npp_cleaning_areas_rp || '',
        npp_cleaning_areas_cow: reportData.npp_cleaning_areas_cow || '',
        npp_emergency_repairs_rm: reportData.npp_emergency_repairs_rm || '',
        npp_emergency_repairs_rp: reportData.npp_emergency_repairs_rp || '',
        npp_emergency_repairs_cow: reportData.npp_emergency_repairs_cow || '',
        npp_gardening_rm: reportData.npp_gardening_rm || '',
        npp_gardening_rp: reportData.npp_gardening_rp || '',
        npp_gardening_cow: reportData.npp_gardening_cow || '',

        // Dates
        date_taken_on: reportData.date_taken_on || property?.date_taken_on || null,
        created_at: reportData.created_at || null,
        date_sign: reportData.date_sign || null,
        key_collection_date: reportData.key_collection_date || '',
      };

      console.log('Transformed PDF Data:', pdfData);

      // Choose the appropriate PDF component
      let pdfDocument;
      if (isMetropolitanHousing) {
        // Use MTVH PDF template
        pdfDocument = (
          <MTVHProposalPDF
            data={pdfData}
            clauses={apiData.clauses || {}}
          />
        );
      } else {
        // Use Standard PDF template
        pdfDocument = <StandardProposalPDF data={pdfData} />;
      }

      console.log('Generating PDF blob...');
      // Generate the PDF blob
      const blob = await pdf(pdfDocument).toBlob();
      console.log('PDF blob generated successfully');

      // Download the PDF
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Property_Proposal_${isMetropolitanHousing ? 'MTVH' : 'Standard'}_${reportId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);

      // Show detailed error message
      const errorMessage = error.response?.data?.message || error.message || 'Failed to generate PDF';
      toast.error(`PDF Generation Error: ${errorMessage}`);
    }
  };

  // Download signed property proposal PDF
  const handleDownloadSignedPropertyProposal = async (reportId) => {
    try {
      const response = await api.get(`/propertyproposals/${reportId}/signed`, {
        responseType: 'blob'
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Property_Proposal_${reportId}_SIGNED.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Signed PDF downloaded successfully!');
    } catch (error) {
      console.error('Error downloading signed PDF:', error);
      toast.error('Failed to download signed PDF');
    }
  };

  // Load reports data
  const loadReports = async () => {
    try {
      // Call the main property data loading function which includes reports
      await loadPropertyData();
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  // Assign Guardian Modal handlers
  const handleAssignGuardian = (room) => {
    console.log('🔧 [DEBUG] handleAssignGuardian called with room:', room);
    console.log('🔧 [DEBUG] Setting selectedRoomForAssignment to:', room);
    console.log('🔧 [DEBUG] Setting isAssignGuardianModalOpen to: true');

    setSelectedRoomForAssignment(room);
    setIsAssignGuardianModalOpen(true);

    // Force a small delay to check state
    setTimeout(() => {
      console.log('🔧 [DEBUG] Modal state after update - isOpen:', isAssignGuardianModalOpen);
      console.log('🔧 [DEBUG] Selected room after update:', selectedRoomForAssignment);
    }, 100);
  };

  const handleCloseAssignGuardianModal = () => {
    setIsAssignGuardianModalOpen(false);
    setSelectedRoomForAssignment(null);
  };

  const handleAssignmentSuccess = (assignmentData) => {
    console.log('Assignment successful:', assignmentData);

    // Refresh property data to reflect the assignment
    loadPropertyData();

    // Note: Navigation is now handled directly in AssignGuardianModal
    // This handler is kept for backward compatibility but navigation happens in modal
  };

  // Handle finish rent - Laravel equivalent: RentsController@finish
  const handleFinishRent = async (rentId) => {
    if (window.confirm('Are you sure you want to finish this rent? This action cannot be undone.')) {
      try {
        const response = await api.get(`/rents/${rentId}/finish`);
        if (response.data.success) {
          toast.success('Rent finished successfully!');
          // Refresh property data to reflect the changes
          loadPropertyData();
        } else {
          toast.error(response.data.message || 'Failed to finish rent');
        }
      } catch (error) {
        console.error('Error finishing rent:', error);
        toast.error(error.response?.data?.message || 'Failed to finish rent');
      }
    }
  };

  // Filter and pagination logic
  const filteredNotes = useMemo(() => {
    let filtered = notes;

    // Apply filter - match Laravel's exact search behavior
    if (activeFilter !== "all") {
      filtered = filtered.filter((note) => {
        // Laravel searches by exact match on the type field
        const noteType = note.type?.toLowerCase();
        const filterType = activeFilter.toLowerCase();
        return noteType === filterType;
      });
    }

    // Apply search
    if (searchTerm) {
      filtered = filtered.filter(
        (note) =>
          note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
          note.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          note.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case "date":
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case "user":
          aValue = a.user_name;
          bValue = b.user_name;
          break;
        case "type":
          aValue = a.type;
          bValue = b.type;
          break;
        case "note":
          aValue = a.content;
          bValue = b.content;
          break;
        default:
          return 0;
      }

      if (sortDirection === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [notes, activeFilter, searchTerm, sortField, sortDirection]);

  // Filter logic for maintenance (search only, for total count)
  const searchedMaintenance = useMemo(() => {
    let filtered = maintenance;

    // Apply search
    if (maintenanceSearchTerm) {
      filtered = filtered.filter((ticket) =>
        ticket.id.toString().includes(maintenanceSearchTerm) ||
        ticket.status?.toLowerCase().includes(maintenanceSearchTerm.toLowerCase()) ||
        ticket.urgency?.toLowerCase().includes(maintenanceSearchTerm.toLowerCase()) ||
        ticket.propertyName?.toLowerCase().includes(maintenanceSearchTerm.toLowerCase()) ||
        ticket.jobType?.toLowerCase().includes(maintenanceSearchTerm.toLowerCase()) ||
        ticket.contractorFirstName?.toLowerCase().includes(maintenanceSearchTerm.toLowerCase()) ||
        ticket.contractorLastName?.toLowerCase().includes(maintenanceSearchTerm.toLowerCase()) ||
        ticket.contractor?.toLowerCase().includes(maintenanceSearchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [maintenance, maintenanceSearchTerm]);

  // Total count for pagination (before slicing)
  const totalMaintenanceCount = searchedMaintenance.length;
  const totalMaintenancePages = Math.ceil(totalMaintenanceCount / maintenancePageSize);

  // Paginated maintenance for display
  const filteredMaintenance = useMemo(() => {
    const startIndex = (maintenanceCurrentPage - 1) * maintenancePageSize;
    const endIndex = startIndex + maintenancePageSize;
    return searchedMaintenance.slice(startIndex, endIndex);
  }, [searchedMaintenance, maintenanceCurrentPage, maintenancePageSize]);

  const totalNotes = filteredNotes.length;
  const totalPages = Math.ceil(totalNotes / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalNotes);
  const paginatedNotes = filteredNotes.slice(startIndex, endIndex);

  console.log("🔍 [RENDER] Component render - Loading:", loading, "Error:", error, "Property:", !!property);
  console.log("🔍 [RENDER] Property data:", property);

  if (loading) return <Loader />;
  if (error) return <div className="alert alert-danger">{error}</div>;
  if (!property) {
    console.log("❌ [RENDER] Property is null/undefined, showing not found");
    return <div>Property not found - Live data not loaded</div>;
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs - Laravel equivalent: Breadcrumbs::render('property-show', $property, $currentUser) */}
      <Breadcrumbs
        items={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Properties', href: isClientUser ? '/dashboard' : '/properties' },
          { label: property?.name || 'Property', active: true }
        ]}
      />

      {/* Property Tabs - Laravel equivalent: _property_tabs.blade.php */}
      {/* Hide all action buttons for CLIENT role */}
      {!isClientUser && !isClientView && (
        <div className={styles.propertyTabs}>
          {hasPermission('properties.edit') && (
            <a
              className="btn-head"
              href={`/properties/${id}/edit`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="glyphicon glyphicon-edit"></i> Edit Property
            </a>
          )}

          {hasPermission('properties.photos.manage') && (
            <a
              className="btn-head"
              href={`/properties/${id}/photos`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Photos
            </a>
          )}

          {hasPermission('payments.list') && (
            <a
              className="btn-head"
              href={`/properties/${id}/payments`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fa fa-money"></i> Payments
            </a>
          )}

          {hasPermission('property_viewings.show') && (
            <a
              className="btn-head"
              href={`/property_viewings/property/${id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Viewings
            </a>
          )}

          {(hasPermission('properties.expenditures.manage') || hasPermission('expenditures.view.access')) && (
            <a
              className="btn-head"
              href={`/properties/${id}/expenditures`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Expenditures
            </a>
          )}

          {hasPermission('inspections.show') && (
            <>
              <a
                className="btn-head"
                href={`/inspections/property/${id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Inspections
              </a>
              <a
                className="btn-head"
                href={`/properties/${id}/monthly-inspections`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Monthly Inspections
              </a>
            </>
          )}

          {hasPermission('properties.liabilities.manage') && (
            <a
              className="btn-head"
              href={`/properties/${id}/liabilities/edit`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Liabilities
            </a>
          )}

          {hasPermission('properties.notice_to_quit') && (
            <>
              {property?.canSetNoticeToQuit && (
                <a
                  className="btn-head"
                  href={`/properties/${id}/notice-to-quit/create`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Give notice on property
                </a>
              )}
              {property?.canManageNoticeToQuit && property?.latestNoticeToQuit && (
                <a
                  className="btn-head"
                  href={`/properties/${id}/notice-to-quit/${property.latestNoticeToQuit.id}/edit`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Manage NTQ
                </a>
              )}
              {(property?.latestNoticeToQuit || property?.latest_notice_to_quit) &&
               (property?.rentNtqLettersCount > 0 || property?.rent_ntq_letters_count > 0) && (
                <a
                  className="btn-head"
                  href={`/properties/${id}/notice-to-quit/guardian-letters`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Guardian NTQ Letters
                </a>
              )}
            </>
          )}
        </div>
      )}

      {/* Laravel equivalent: CLIENT role sees read-only view */}
      {(isClientUser || isClientView) && (
        <>
          {/* <div className={styles.clientNotice}>
            <div className="alert alert-info">
              <i className="glyphicon glyphicon-info-sign"></i>
              <strong>Client View:</strong> You are viewing this property with read-only access.
            </div>
          </div> */}

          {/* CLIENT-specific buttons - Laravel equivalent from show_for_client.blade.php */}
          <div className={styles.clientButtons}>
            {/* Photos button - always visible for CLIENT users (line 25 in Laravel) */}
            <a
              className="btn btn-secondary"
              href={`/properties/${id}/photos`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Photos
            </a>

            {/* Give notice on property button - HIDDEN for now as requested */}
            {/*
            {hasPermission('properties.notice_to_quit') &&
             property?.canSetNoticeToQuit && (
              <a
                className="btn btn-secondary ml-2"
                href={`/properties/${id}/notice-to-quit/create`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Give notice on property
              </a>
            )}
            */}
          </div>
        </>
      )}

      {/* Second row buttons - Laravel equivalent: Hidden for CLIENT role */}
      {!isClientUser && !isClientView && (
        <div className={styles.reportButtons}>
          {/* Client brief: navy ADD METER button at the top of the property
              page. Opens the same add form as the Meter details panel. */}
          <a
            className="btn-head"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setIsAddMeterModalOpen(true);
            }}
          >
            <i className="glyphicon glyphicon-plus"></i> ADD METER
          </a>

          {hasPermission('properties.reports.monthly_inspection') && (
            <a
              className="btn-head"
              href={`/properties/${id}/reports/monthly_inspections/create`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="glyphicon glyphicon-edit"></i> Create Monthly Inspection
              Report
            </a>
          )}

          {hasPermission('properties.reports.initial_inspection') && (
            <a
              className="btn-head"
              href={`/properties/${id}/reports/initial_inspections/create`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="glyphicon glyphicon-edit"></i> Create Initial Inspection
              Report
            </a>
          )}

          {hasPermission('properties.reports.monthly_inspection') && (property?.status === 11 || property?.status_id === 11 || property?.status === 'Key Holding' || property?.status_text === 'Key Holding') && (
            <a
              className="btn-head"
              href={`/properties/${id}/reports/monthly_inspections/create?weekly=1`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="glyphicon glyphicon-edit"></i> Create Weekly
              Inspection Report
            </a>
          )}

          {hasPermission('properties.reports.property_proposal') && (
            <a
              className="btn-head"
              href={`/properties/${id}/reports/property_proposals/create`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="glyphicon glyphicon-edit"></i> Create Property Proposal
            </a>
          )}

          {hasPermission('properties.emails.guardians') && (
            <a
              className="btn-head"
              href={`/properties/${id}/emails/guardians/create`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="fa fa-envelope"></i> Email guardians
            </a>
          )}

          {/* Copy address button - Laravel equivalent: Hidden for CLIENT users */}
          <a
            className="btn-head"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              copyAddress();
            }}
          >
            <i className="glyphicon glyphicon-road"></i> Copy address
          </a>
        </div>
      )}

      {/* Basic Info Section */}
      <div className={styles.row}>
        <div className={styles.colMd6}>
          {loading ? (
            <div className="panel">
              <div className="panel-title title-standard">
                <h3>Property overview</h3>
              </div>
              <div className="panel-body">
                <div className="text-center">
                  <Loader />
                  <p>Loading property details...</p>
                </div>
              </div>
            </div>
          ) : property ? (
            <div className="panel">
              <div className="panel-title title-standard">
                <h3>Property overview</h3>
              </div>
              <div className="panel-body">
                <table className={styles.tableList}>
                  <tbody>
                    {/* Laravel: Hide property_id from guardians - _property_overview.blade.php:2 */}
                    {!isGuardianUser && (
                      <tr>
                        <td className={styles.tableListHeader}>Property ID:</td>
                        <td>{property.property_id}</td>
                      </tr>
                    )}
                    <tr>
                      <td className={styles.tableListHeader}>Property Name:</td>
                      <td>{property.name}</td>
                    </tr>
                    <tr>
                      <td className={styles.tableListHeader}>
                        Property Status:
                      </td>
                      <td>
                        {property.status_text ||
                          property.status ||
                          "Not specified"}
                      </td>
                    </tr>
                    {/* Sits next to Status, matching where it appears on the
                        property edit form. Drives the Building Set Up KPI
                        deadline: 14 calendar days residential, 28 commercial. */}
                    <tr>
                      <td className={styles.tableListHeader}>
                        Residential/Commercial:
                      </td>
                      <td>
                        {property.residential_commercial === "residential"
                          ? "Residential"
                          : property.residential_commercial === "commercial"
                          ? "Commercial"
                          : "Not specified"}
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.tableListHeader}>
                        Property address:
                      </td>
                      <td>
                        {property.address_1}
                        {property.address_2 && (
                          <>
                            <br />
                            {property.address_2}
                          </>
                        )}
                        {property.address_3 && (
                          <>
                            <br />
                            {property.address_3}
                          </>
                        )}
                        {property.town && (
                          <>
                            <br />
                            {property.town}
                          </>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.tableListHeader}>Postcode:</td>
                      <td>{property.postcode || "Not specified"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : error ? (
            <div className="panel">
              <div className="panel-title title-standard">
                <h3>Property overview</h3>
              </div>
              <div className="panel-body">
                <div className="alert alert-danger">
                  <strong>Error:</strong> {error}
                </div>
              </div>
            </div>
          ) : (
            <div className="panel">
              <div className="panel-title title-standard">
                <h3>Property overview</h3>
              </div>
              <div className="panel-body">
                <div className="alert alert-danger">
                  <strong>Error:</strong> Failed to load property details.
                </div>
              </div>
            </div>
          )}

          {client && (
            <div className="panel">
              <div className="panel-title title-standard">
                <h3>Property company</h3>
              </div>
              <div className="panel-body">
                <table className={styles.tableList}>
                  <tbody>
                    <tr>
                      <td className={styles.tableListHeader}>Company:</td>
                      <td>{client?.name || "Not specified"}</td>
                    </tr>
                    <tr>
                      <td className={styles.tableListHeader}>Type:</td>
                      {/* client_type is stored as a code; client_type_text is
                          the readable name. The raw code used to show here. */}
                      <td>
                        {property?.client_type_text ||
                          property?.client_type ||
                          "Not specified"}
                      </td>
                    </tr>
                    {clientContact && (
                      <>
                        <tr>
                          <td className={styles.tableListHeader}>
                            Company contact:
                          </td>
                          <td>
                            {clientContact?.user?.fullName || "Not specified"}
                          </td>
                        </tr>
                        {clientContact?.user?.phone_number && (
                          <tr>
                            <td className={styles.tableListHeader}>
                              Phone number:
                            </td>
                            <td>{clientContact.user.phone_number}</td>
                          </tr>
                        )}
                        <tr>
                          <td className={styles.tableListHeader}>E-mail:</td>
                          <td>
                            {clientContact?.user?.email || "Not specified"}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Right Column for CLIENT users - Property savings & voids into profit to date */}
        {(isClientUser || isClientView) && !property?.isMetropolitanHousing && (
          <div className={styles.colMd6}>
            <div className="panel">
              <div className="panel-title title-standard">
                <h3>Property savings & voids into profit to date</h3>
              </div>
              <div className="panel-body">
                <div>
                  {/* Red Savings Box */}
                  <div className={`${styles.savingBox} ${styles.savingBoxRed} ${styles.stacked}`}>
                    <img src="/images/peppa-red.png" alt="Savings" />
                    <p className={styles.cash}>+£{formatCurrency(property?.savings_made)}</p>
                    <p className={styles.savingInformation}>
                      SAVINGS MADE BY USING GLOBAL GUARDIANS
                    </p>
                  </div>

                  {/* Green VIP Box */}
                  <div className={`${styles.savingBox} ${styles.savingBoxMint} ${styles.stacked}`}>
                    <img src="/images/hand-mint.png" alt="VIP" />
                    <p className={styles.cash}>+£{property?.vip_amount ? parseFloat(property.vip_amount).toFixed(2) : '0.00'}</p>
                    <p className={styles.savingInformation}>
                      INCOME GENERATED THROUGH VOIDS INTO PROFIT
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Account Manager and Building Manager - Hidden from CLIENT users (Laravel: show_for_client.blade.php doesn't include these) */}
        {!isClientUser && !isClientView && (
          <div className={styles.colMd6}>
            <div className="panel">
              <div className="panel-title title-standard">
                <h3>Account Manager</h3>
              </div>
              <div className="panel-body">
                {accountManager ? (
                  <table className={styles.tableList}>
                    <tbody>
                      <tr>
                        <td className={styles.tableListHeader}>Name:</td>
                        <td>{accountManager.fullName || "Not specified"}</td>
                      </tr>
                      <tr>
                        <td className={styles.tableListHeader}>Phone number:</td>
                        <td>{accountManager.phone_number || "Not specified"}</td>
                      </tr>
                      <tr>
                        <td className={styles.tableListHeader}>E-mail:</td>
                        <td>{accountManager.email || "Not specified"}</td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <p>No account manager assigned.</p>
                )}
              </div>
            </div>

            {buildingManager && (
              <div className="panel">
                <div className="panel-title title-standard">
                  <h3>Building Manager</h3>
                </div>
                <div className="panel-body">
                  <table className={styles.tableList}>
                    <tbody>
                      <tr>
                        <td className={styles.tableListHeader}>Name:</td>
                        <td>{buildingManager.fullName || "Not specified"}</td>
                      </tr>
                      <tr>
                        <td className={styles.tableListHeader}>Phone number:</td>
                        <td>{buildingManager.phone_number || "Not specified"}</td>
                      </tr>
                      <tr>
                        <td className={styles.tableListHeader}>E-mail:</td>
                        <td>{buildingManager.email || "Not specified"}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Laravel permission check: properties.all_info + CLIENT role check - wraps multiple sections */}
      {hasPermission('properties.all_info') && !isClientUser && !isClientView && (
        <>
          {/* Property Notes Section */}
          <div className="panel">
            <div className="panel-title title-standard">
              <h3>Property Notes</h3>
              <div className={styles.reportButtons}>
                <a
                  className="btn btn-secondary"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleAddNote();
                  }}
                >
                  <i className="glyphicon glyphicon-plus"></i> Add note
                </a>
                <a
                  className={`btn btn-secondary ${activeFilter === "property_note" ? "active" : ""
                    }`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveFilter("property_note");
                  }}
                >
                  Property Notes
                </a>
                <a
                  className={`btn btn-secondary ${activeFilter === "fit_out_note" ? "active" : ""
                    }`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveFilter("fit_out_note");
                  }}
                >
                  Fit Out Notes
                </a>
                <a
                  className={`btn btn-secondary ${activeFilter === "utility_note" ? "active" : ""
                    }`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveFilter("utility_note");
                  }}
                >
                  Utilities Notes
                </a>
                <a
                  className={`btn btn-secondary ${activeFilter === "all" ? "active" : ""
                    }`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setActiveFilter("all");
                  }}
                >
                  Show all
                </a>
              </div>
            </div>
            <div className="panel-body">
              {/* Search and Filter Controls */}
              <div className={styles.tableToolbar}>
                <div className={styles.tableToolbarEntries}>
                  <label>
                    Show
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(parseInt(e.target.value))}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    entries
                  </label>
                </div>
                <div className={styles.tableToolbarSearch}>
                  <label>
                    Search:
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </label>
                </div>
              </div>

              {/* Notes Table - Match Laravel DataTable structure */}
              <div className="dataTable_wrapper">
                <table className="table table-hover dt-responsive table-striped table-bordered">
                  <thead>
                    <tr>
                      <th style={{ display: 'none' }}>ID</th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("date")}
                        className="sorting"
                      >
                        Date <i className={`glyphicon ${getSortIcon("date")}`}></i>
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("user")}
                        className="sorting"
                      >
                        User <i className={`glyphicon ${getSortIcon("user")}`}></i>
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("type")}
                        className="sorting"
                      >
                        Type <i className={`glyphicon ${getSortIcon("type")}`}></i>
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("note")}
                        className="sorting"
                      >
                        Note <i className={`glyphicon ${getSortIcon("note")}`}></i>
                      </th>
                      <th className="action-head">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedNotes.map((note) => (
                      <tr key={note.id}>
                        <td style={{ display: 'none' }}>{note.id}</td>
                        <td>{formatDateTime(note.created_at)}</td>
                        <td>{note.user_name}</td>
                        <td>{note.type}</td>
                        <td>{note.content}</td>
                        <td className="action">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleEditNote(note.id)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger btn-sm ml-2"
                            onClick={() => handleDeleteNote(note.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="row">
                <div className="col-sm-6">
                  <p>
                    Showing {startIndex + 1} to {endIndex} of {totalNotes} entries.
                  </p>
                </div>
                <div className="col-sm-6 text-right">
                  <button
                    className="btn btn-default btn-sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button className="btn btn-success btn-sm ml-2" disabled>
                    {currentPage}
                  </button>
                  <button
                    className="btn btn-default btn-sm ml-2"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Current Vacancies Section */}
          <div className="panel">
            <div className="panel-title title-standard">
              <h3>Current vacancies</h3>
              <div className={styles.reportButtons}>
                <a
                  className="btn btn-secondary"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsAddRoomModalOpen(true);
                  }}
                >
                  <i className="glyphicon glyphicon-plus"></i> Add room
                </a>
                <a
                  className="btn btn-secondary"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsManageRoomsModalOpen(true);
                  }}
                >
                  <img src="/assets/images/edit.png" alt="Edit" />
                </a>
              </div>
            </div>
            <div className="panel-body">
              {rooms.length > 0 ? (
                <div className="row">
                  {rooms.map((room, index) => (
                    <div key={`${room.id}-${index}`} className="col-xs-12 col-sm-6 col-md-2">
                      <div className={`room-information ${(room.status === "NOTICE_GIVEN" && room.rent) || (room.rent && room.rent.notice_to_quit) ? 'room-notice-given' : (room.roomClass || '')}`} style={{ marginBottom: '20px' }}>
                        {room.head_guardian === 1 && (
                          <span className="head_guardian">Head Guardian</span>
                        )}
                        <p className="room-number">{room.name}</p>
                        <div style={{ display: 'none' }} id={`room-id-${room.id}`}>{room.id}</div>
                        
                        {/* Display Room status - matching Laravel exactly */}
                        {/* Use backend-calculated status (matches Laravel _room.blade.php logic) */}
                        <span className="status">
                          {/* Render-time guard: only badge "NOTICE GIVEN" when a rent is
                              actually in place (guardian under notice). A stale
                              rooms.status_id=2 on an empty room must not surface here. */}
                          {(room.status === "NOTICE_GIVEN" && room.rent) || (room.rent && room.rent.notice_to_quit)
                            ? "NOTICE GIVEN"
                            : room.status === "MAINTENANCE"
                            ? "MAINTENANCE"
                            : room.status === "OCCUPIED"
                            ? "OCCUPIED"
                            : room.status === "VACANT"
                            ? "VACANT"
                            : "VACANT"}
                        </span>

                        <ul className="room-information-in" id={`room-information-in-${room.id}`}>
                          {/* Display guardian link if assigned to room */}
                          {room.rent && room.guardian ? (
                            <li>
                              <i className="glyphicon glyphicon-user"></i>
                              <a href={`/guardians/${room.guardian.id}`} target="_blank" rel="noopener noreferrer">
                                {room.guardian.fullName || 'N/A'}
                              </a>
                            </li>
                          ) : (
                            <li><i className="glyphicon glyphicon-user"></i>Empty room</li>
                          )}

                          {/* Rate - only show if user has permission (simplified for now) */}
                          {/* Laravel: &pound; {{ $rent->monthly_payment }} */}
                          <li>
                            <span>Rate: </span>
                            {room.rent ? `£ ${Number(room.rent.monthly_payment) || 0}` : '-'}
                          </li>

                          <li>
                            <span>Start: </span>
                            {room.rent && room.rent.dateStartShow ? room.rent.dateStartShow : '-'}
                          </li>
                          <li>
                            <span>End: </span>
                            {room.rent && room.rent.dateEndShow ? room.rent.dateEndShow : '-'}
                          </li>
                          {room.rent && room.rent.notice_to_quit && (
                            <li className="fs-13">
                              <span>Not.: </span>
                              {room.rent.notice_to_quit.dateNoticeShow || '-'}
                            </li>
                          )}
                          <li>
                            <span>Keys: </span>
                            {room.key_number || 0}
                          </li>

                          {room.exclude_display === 1 && (
                            <li><span>Excluded</span></li>
                          )}

                          {/* If room can have guardian assigned show assign button */}
                          {/* Laravel: @if ($room->isTypeGuardian() && $room->canHaveGuardianAssigned()) */}
                          {/* This shows for BOTH vacant AND occupied rooms (if they can have another guardian) */}
                          {room.type_id === 1 && room.canHaveGuardianAssigned && (
                            <li className="smaller hidden-print">
                              <a href="#" onClick={(e) => {
                                e.preventDefault();
                                handleAssignGuardian(room);
                              }} id={`assign-button-${room.id}`}>
                                <span><i className="glyphicon glyphicon-menu-right"></i></span> Assign guardian
                              </a>
                            </li>
                          )}

                          {/* If room has rent, show Finish, Give notice, and Manage NTQ buttons */}
                          {/* Laravel: @if(isset($rent)) */}
                          {room.rent && (
                            <>
                              {/* Finish button - Laravel: @if(in_array('rents.edit', $globalPermissions)) */}
                              {hasPermission('rents.edit') && (
                                <li className="smaller hiddn-print">
                                  <a
                                    href="#"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleFinishRent(room.rent.id);
                                    }}
                                  >
                                    <span><i className="glyphicon glyphicon-menu-right"></i></span> Finish
                                  </a>
                                </li>
                              )}

                              {/* Give notice button - Laravel: @if(in_array('rents.notice_to_quit.create', $globalPermissions) && !$rent->notice_to_quit) */}
                              {hasPermission('rents.notice_to_quit.create') && !room.rent.notice_to_quit && (
                                <li className="smaller hiddn-print">
                                  <a
                                    href={`/rents/${room.rent.id}/notice-to-quit/create`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <span><i className="glyphicon glyphicon-menu-right"></i></span> Give notice
                                  </a>
                                </li>
                              )}

                              {/* Manage NTQ button - Laravel: @if(in_array('rents.notice_to_quit.manage', $globalPermissions) && $rent->notice_to_quit) */}
                              {hasPermission('rents.notice_to_quit.manage') && room.rent.notice_to_quit && (
                                <li className="smaller hiddn-print">
                                  <a
                                    href={`/rents/${room.rent.id}/notice-to-quit/edit`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    <span><i className="glyphicon glyphicon-menu-right"></i></span> Manage NTQ
                                  </a>
                                </li>
                              )}
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>No rooms available.</p>
              )}
            </div>
          </div>

          {/* Meter Details Section */}
          <div className="panel">
            <div className="panel-title title-standard">
              <h3>Meter details</h3>
              <div className={styles.reportButtons}>
                <a
                  className="btn btn-primary"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsAddMeterModalOpen(true);
                  }}
                >
                  <i className="glyphicon glyphicon-plus"></i> ADD METER
                </a>
                <a
                  className="btn btn-secondary"
                  href="#"
                  title="Manage meters"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsManageMetersModalOpen(true);
                  }}
                >
                  <img src="/assets/images/edit.png" alt="Manage meters" />
                </a>
              </div>
            </div>
            <div className="panel-body">
              {meters.length > 0 ? (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Serial number</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meters.map((meter) => (
                      <tr key={meter.id}>
                        <td>{meter.meter_type}</td>
                        <td>{meter.serial_number}</td>
                        <td>{meter.location}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : noMetersConfirmed ? (
                <p>
                  This property is recorded as having no meters. Monthly
                  inspections will not ask for readings.
                </p>
              ) : (
                <p>
                  No meters added yet. Meters added here appear automatically on
                  every new monthly inspection report.
                </p>
              )}
            </div>
          </div>

          {/* Liabilities Section */}
          <div className="panel">
            <div className="panel-title title-standard">
              <h3>Liabilities</h3>
              {hasPermission("properties.liabilities.manage") && (
                <a
                  className="btn btn-secondary"
                  href={`/properties/${id}/liabilities/edit`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="glyphicon glyphicon-pencil"></i> Edit
                </a>
              )}
            </div>
            <div className="panel-group border-down margin-down">
              <table className={styles.tableList} style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th>Liability</th>
                    <th>Work</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {liabilities && liabilities.length > 0 ? (
                    liabilities.map((liability) => (
                      <tr key={liability.id}>
                        <td>{liability.type?.name || "Not specified"}</td>
                        <td>{liability.work?.name || "Not specified"}</td>
                        <td>{liability.cost?.name || "Not specified"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3">No liabilities found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Past Building Set Up rounds. Kept for audit at the client's
              request: the BSU table only shows the current round. */}
          {bsuHistory.length > 0 && (
            <div className="panel">
              <div className="panel-title title-standard">
                <h3>Building Set Up history</h3>
              </div>
              <div className="panel-body">
                <table className={styles.tableList}>
                  <thead>
                    <tr>
                      <th>Round</th>
                      <th>Started</th>
                      <th>Completed</th>
                      <th>KPI deadline</th>
                      <th>Days taken</th>
                      <th>Outcome</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bsuHistory.map((round) => (
                      <tr key={round.round_number}>
                        <td>{round.round_number}</td>
                        <td>
                          {round.started_at
                            ? new Date(round.started_at).toLocaleDateString("en-GB")
                            : "—"}
                        </td>
                        <td>
                          {round.completed_at
                            ? new Date(round.completed_at).toLocaleDateString("en-GB")
                            : "—"}
                        </td>
                        <td>
                          {round.kpi_deadline
                            ? new Date(round.kpi_deadline).toLocaleDateString("en-GB")
                            : "—"}
                        </td>
                        <td>
                          {round.days_taken !== null && round.days_taken !== undefined
                            ? round.days_taken
                            : "—"}
                        </td>
                        <td>
                          {round.outcome === "completed"
                            ? round.kpi_met === 1
                              ? "Completed within KPI"
                              : round.kpi_met === 0
                              ? "Completed late"
                              : "Completed"
                            : "Restarted before completion"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Accommodation Section */}
          <div className="panel">
            <div className="panel-title title-standard">
              <h3>Accommodation</h3>
            </div>
            <div className="panel-group border-down margin-down">
              <div className="col-md-4">
                <dl className="list green-list green-list-with-width">
                  <dt>Price range:</dt>
                  <dd>
                    £{property.price_from || "0"} to £{property.price_to || "0"}
                  </dd>
                  {Number(property.actual_market_rent) > 0 && (
                      <>
                        <dt>Actual market rent:</dt>
                        <dd>£{Number(property.actual_market_rent)}</dd>
                      </>
                    )}
                  {Number(property.square_footage) > 0 && (
                    <>
                      <dt>Square footage:</dt>
                      <dd>{Number(property.square_footage)}</dd>
                    </>
                  )}
                  {Number(property.rateable_value) > 0 && (
                    <>
                      <dt>Rateable value:</dt>
                      <dd>{Number(property.rateable_value)}</dd>
                    </>
                  )}
                  {Number(property.rebuild_value) > 0 && (
                    <>
                      <dt>Rebuild value:</dt>
                      <dd>{Number(property.rebuild_value)}</dd>
                    </>
                  )}
                  {property.location && (
                    <>
                      <dt>Location:</dt>
                      <dd>{property.location.name}</dd>
                    </>
                  )}
                  {property.type && (
                    <>
                      <dt>Type:</dt>
                      <dd>{property.type.name}</dd>
                    </>
                  )}
                  <dt>Spaces:</dt>
                  <dd>{parseInt(property.spaces, 10) || 0}</dd>
                  <dt>Bathrooms:</dt>
                  <dd>{parseInt(property.bathrooms, 10) || 0}</dd>
                  <dt>Kitchens:</dt>
                  <dd>{parseInt(property.kitchens, 10) || 0}</dd>
                  <dt>Garden:</dt>
                  <dd>{property.garden ? "Yes" : "No"}</dd>
                  <dt>Parking:</dt>
                  <dd>{property.parking ? "Yes" : "No"}</dd>
                </dl>
              </div>
              <div className="col-md-8">
                <dl className="list green-list green-list-with-width width-100">
                  {property.photos && property.photos.length > 0 && (
                    <>
                      <dt>Photos:</dt>
                      <dd>
                        <div className="owl-carousel owl-theme">
                          <div className="item">
                            <div className="row">
                              {property.photos.map((photo, index) => (
                                <div key={index} className="col-xs-3">
                                  <div className="mb-10">
                                    <a
                                      className="fancybox"
                                      rel="gallery1"
                                      href={photo.name}
                                    >
                                      <img
                                        src={photo.name}
                                        className="img img-responsive img-property"
                                        alt="Property"
                                      />
                                    </a>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </dd>
                    </>
                  )}
                </dl>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Associated Documents Section - Laravel: _associated_documents.blade.php (visible to all, but actions permission-gated) */}
      <div className="panel">
        <div className="panel-title title-standard">
          <h3>Property documents</h3>
          {/* Laravel: Add document/certificate buttons require 'properties.documents.create' permission */}
          {hasPermission('properties.documents.create') && (
            <div className={styles.reportButtons}>
              <a
                className="btn btn-secondary"
                href={`/properties/${id}/documents/create`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="glyphicon glyphicon-plus"></i> Add document
              </a>
              <a
                className="btn btn-secondary"
                href={`/properties/${id}/certificate/create`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="glyphicon glyphicon-plus"></i> Add certificate
              </a>
              <a
                className="btn btn-secondary"
                href={`/properties/${id}/documents`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src="/assets/images/edit.png" alt="Edit" />
              </a>
            </div>
          )}
        </div>
        <div className="panel-body">
          {/* Certificates */}
          <h4 className={styles.reportSectionHeader}>Certificates</h4>
          {certificates.length > 0 ? (
            <div className={styles.documentsGrid}>
              {certificates.map((certificate) => (
                <div key={certificate.id} className={styles.documentCard}>
                  <div className={styles.documentLeft}>
                    <img src="/images/pdf.png" alt="PDF" />
                  </div>
                  <div className={styles.documentRight}>
                    <p className={styles.documentTitle}>
                      {certificate.name_or_type}
                    </p>
                    <p className={styles.documentShort}>
                      <span>SUBMITTED: </span>
                      {certificate.date_uploaded}
                    </p>
                    {certificate.date_expiration && (
                      <p className={styles.documentShort}>
                        <span>EXPIRATION: </span>
                        {certificate.date_expiration}
                      </p>
                    )}
                  </div>
                  <div className={styles.documentActions}>
                    <button
                      className={styles.documentAction}
                      onClick={() => handleShowDocumentPreview(certificate)}
                    >
                      Show
                    </button>
                    <button
                      className={styles.documentAction}
                      onClick={() => handleDownloadDocument(certificate.id, certificate.name_or_type)}
                    >
                      Download
                    </button>
                    {/* Laravel equivalent: CLIENT users should NOT see Edit/Archive/Remove buttons */}
                    {!isClientUser && !isClientView && (
                      <>
                        <button
                          className={styles.documentAction}
                          onClick={() => handleEditDocument(certificate.id, certificate.name_or_type)}
                        >
                          Edit
                        </button>
                        <button
                          className={styles.documentAction}
                          onClick={() => handleArchiveDocument(certificate.id, certificate.name_or_type)}
                        >
                          Archive
                        </button>
                        <button
                          className={`${styles.documentAction} ${styles.documentActionRemove}`}
                          onClick={() => handleRemoveDocument(certificate.id, certificate.name_or_type)}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p>There are no certificates.</p>
          )}

          {/* Archived Certificates */}
          {archivedCertificates.length > 0 && (
            <>
              <h4 className={styles.reportSectionHeader}>Archived Certificates</h4>
              <div className={styles.documentsGrid}>
                {archivedCertificates.map((certificate) => (
                  <div key={certificate.id} className={styles.documentCard} style={{ opacity: 0.6 }}>
                    <div className={styles.documentLeft}>
                      <img src="/images/pdf.png" alt="PDF" />
                    </div>
                    <div className={styles.documentRight}>
                      <p className={styles.documentTitle}>
                        {certificate.name_or_type || certificate.name}
                      </p>
                      <p className={styles.documentShort}>
                        <span>SUBMITTED: </span>
                        {certificate.date_uploaded || new Date(certificate.created_at).toLocaleDateString()}
                      </p>
                      {certificate.date_expiration && (
                        <p className={styles.documentShort}>
                          <span>EXPIRATION: </span>
                          {certificate.date_expiration}
                        </p>
                      )}
                    </div>
                    <div className={styles.documentActions}>
                      <button
                        className={styles.documentAction}
                        onClick={() => handleActivateDocument(certificate.id, certificate.name_or_type || certificate.name)}
                        style={{ backgroundColor: '#5cb85c', color: 'white' }}
                      >
                        Activate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Bills */}
          <h4 className={styles.reportSectionHeader}>Bills</h4>
          {bills.length > 0 ? (
            <div className={styles.documentsGrid}>
              {bills.map((bill) => {
                console.log('🔍 Rendering bill card:', { id: bill.id, name: bill.name, downloadUrl: bill.downloadUrl });
                return (
                  <div key={bill.id} className={styles.documentCard}>
                    {/* Laravel: Entire card is clickable download link - documents._list.blade.php line 6 */}
                    <div
                      onClick={() => {
                        console.log('🖱️ Bill card CLICKED:', { id: bill.id, name: bill.name, downloadUrl: bill.downloadUrl });
                        handleDownloadDocument(bill.id, bill.name, bill.downloadUrl);
                      }}
                      style={{ cursor: 'pointer', display: 'flex', flex: 1 }}
                    >
                      <div className={styles.documentLeft}>
                        <img src="/images/pdf.png" alt="PDF" />
                      </div>
                      <div className={styles.documentRight}>
                        <p className={styles.documentTitle}>{bill.name}</p>
                        <p className={styles.documentShort}>
                          <span>SUBMITTED: </span>
                          {bill.date_uploaded}
                        </p>
                      </div>
                    </div>
                    {/* Laravel: Edit/Remove buttons only for STAFF with permissions - documents._list.blade.php lines 19-28 */}
                    {!isClientUser && !isClientView && (
                      <div className={styles.documentActions}>
                        <button
                          className={styles.documentAction}
                          onClick={() => handleEditDocument(bill.id, bill.name)}
                        >
                          Edit
                        </button>
                        <button
                          className={`${styles.documentAction} ${styles.documentActionRemove}`}
                          onClick={() => handleRemoveDocument(bill.id, bill.name)}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p>There are no documents.</p>
          )}

          {/* Reports */}
          <h4 className={styles.reportSectionHeader}>Reports</h4>
          {reports.length > 0 ? (
            <div className={styles.documentsGrid}>
              {reports.map((report) => {
                console.log('🔍 Rendering report card:', { id: report.id, name: report.name, downloadUrl: report.downloadUrl });
                return (
                  <div key={report.id} className={styles.documentCard}>
                    {/* Laravel: Entire card is clickable download link - documents._list.blade.php line 6 */}
                    <div
                      onClick={() => {
                        console.log('🖱️ Report card CLICKED:', { id: report.id, name: report.name, downloadUrl: report.downloadUrl });
                        handleDownloadDocument(report.id, report.name, report.downloadUrl);
                      }}
                      style={{ cursor: 'pointer', display: 'flex', flex: 1 }}
                    >
                      <div className={styles.documentLeft}>
                        <img src="/images/pdf.png" alt="PDF" />
                      </div>
                      <div className={styles.documentRight}>
                        <p className={styles.documentTitle}>{report.name}</p>
                        <p className={styles.documentShort}>
                          <span>SUBMITTED: </span>
                          {report.date_uploaded}
                        </p>
                      </div>
                    </div>
                    {/* Laravel: Edit/Remove buttons only for STAFF with permissions - documents._list.blade.php lines 19-28 */}
                    {!isClientUser && !isClientView && (
                      <div className={styles.documentActions}>
                        <button
                          className={styles.documentAction}
                          onClick={() => handleEditDocument(report.id, report.name)}
                        >
                          Edit
                        </button>
                        <button
                          className={`${styles.documentAction} ${styles.documentActionRemove}`}
                          onClick={() => handleRemoveDocument(report.id, report.name)}
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p>There are no documents.</p>
          )}

          {/* Other */}
          <h4 className={styles.reportSectionHeader}>Other</h4>
          <div className={styles.documentsGrid}>
            {/* Static Documents - Always shown */}
            <div className={styles.documentCard}>
              <div className={styles.documentLeft}>
                <img src="/images/pdf.png" alt="PDF" />
              </div>
              <div className={styles.documentRight}>
                <p className={styles.documentTitle}>On boarding process</p>
                <p className={styles.documentShort}>Click to open</p>
              </div>
              <div className={styles.documentActions}>
                <button
                  className={styles.documentAction}
                  onClick={() => handleStaticDocumentDownload('onboarding_process', 'On boarding process')}
                  disabled={downloading.onboarding_process}
                >
                  {downloading.onboarding_process ? 'Downloading...' : 'Download'}
                </button>
              </div>
            </div>

            <div className={styles.documentCard}>
              <div className={styles.documentLeft}>
                <img src="/images/pdf.png" alt="PDF" />
              </div>
              <div className={styles.documentRight}>
                <p className={styles.documentTitle}>Hand back process</p>
                <p className={styles.documentShort}>Click to open</p>
              </div>
              <div className={styles.documentActions}>
                <button
                  className={styles.documentAction}
                  onClick={() => handleStaticDocumentDownload('handback_process', 'Hand back process')}
                  disabled={downloading.handback_process}
                >
                  {downloading.handback_process ? 'Downloading...' : 'Download'}
                </button>
              </div>
            </div>

            {/* Dynamic Documents from Database */}
            {other.map((doc, index) => {
              console.log('🔍 Rendering other doc card:', { id: doc.id, name: doc.name, downloadUrl: doc.downloadUrl });
              return (
                <div key={doc.id} className={styles.documentCard}>
                  {/* Laravel: Entire card is clickable download link - documents._list.blade.php line 6 */}
                  <div
                    onClick={() => {
                      console.log('🖱️ Other doc card CLICKED:', { id: doc.id, name: doc.name, downloadUrl: doc.downloadUrl });
                      handleDownloadDocument(doc.id, doc.name, doc.downloadUrl);
                    }}
                    style={{ cursor: 'pointer', display: 'flex', flex: 1 }}
                  >
                    <div className={styles.documentLeft}>
                      <img src="/images/pdf.png" alt="PDF" />
                    </div>
                    <div className={styles.documentRight}>
                      <p className={styles.documentTitle}>{doc.name}</p>
                      <p className={styles.documentShort}>
                        <span>SUBMITTED: </span>
                        {doc.date_uploaded}
                      </p>
                    </div>
                  </div>
                  {/* Laravel: Edit/Remove buttons only for STAFF with permissions - documents._list.blade.php lines 19-28 */}
                  {!isClientUser && !isClientView && (
                    <div className={styles.documentActions}>
                      <button
                        className={styles.documentAction}
                        onClick={() => handleEditDocument(doc.id, doc.name)}
                      >
                        Edit
                      </button>
                      <button
                        className={`${styles.documentAction} ${styles.documentActionRemove}`}
                        onClick={() => handleRemoveDocument(doc.id, doc.name)}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Property Reports Section - Laravel: _property_reports.blade.php (visible to all, but actions permission-gated) */}
      <div className="panel">
        <div className="panel-title title-standard">
          <h3>Property reports</h3>
          {/* Laravel: Create report buttons require specific permissions per report type */}
          {(hasPermission('properties.reports.monthly_inspection') ||
            hasPermission('properties.reports.initial_inspection') ||
            hasPermission('properties.reports.property_proposal')) && (
              <div className={styles.reportButtons}>
                {hasPermission('properties.reports.monthly_inspection') && (
                  <a
                    className="btn btn-secondary"
                    href={`/properties/${id}/reports/monthly_inspections/create`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="glyphicon glyphicon-plus"></i> Add MIR
                  </a>
                )}
                {hasPermission('properties.reports.initial_inspection') && (
                  <a
                    className="btn btn-secondary"
                    href={`/properties/${id}/reports/initial_inspections/create`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="glyphicon glyphicon-plus"></i> Add IIR
                  </a>
                )}
                {hasPermission('properties.reports.property_proposal') && (
                  <a
                    className="btn btn-secondary"
                    href={`/properties/${id}/reports/property_proposals/create`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="glyphicon glyphicon-plus"></i> Add PP
                  </a>
                )}
              </div>
            )}
        </div>
        <div className="panel-body">
          {/* Monthly Inspection Reports */}
          {/* Laravel equivalent: CLIENT users only see published reports (status_id === 1) */}
          {(() => {
            // Filter reports for CLIENT users - only show published (status_id === 1)
            const filteredMonthlyInspectionReports = (isClientUser || isClientView)
              ? (propertyReports.monthlyInspectionReports || []).filter(report => report.status_id === 1)
              : (propertyReports.monthlyInspectionReports || []);

            return (
              <>
                <div className={styles.reportSectionHeader}>
                  Monthly Inspection Reports
                </div>
                {filteredMonthlyInspectionReports.length > 0 ? (
                  <div className={styles.reportsGrid}>
                    {filteredMonthlyInspectionReports.map((report) => (
                <div
                  key={report.id}
                  className={styles.reportCard}
                  onClick={() => handleDownloadMonthlyInspectionPDF(report.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className={styles.reportLeft}>
                    <img src="/images/pdf.png" alt="PDF" />
                  </div>
                  <div className={styles.reportRight}>
                    <p className={styles.reportTitle}>
                      {report.monthName} ({report.year})
                    </p>
                    <p className={styles.reportShort}>
                      <span>Updated: </span>
                      {report.updated_at}
                    </p>
                  </div>
                  <div className={styles.reportActions}>
                    {/* Laravel equivalent: CLIENT users should NOT see Edit/Remove buttons */}
                    {!isClientUser && !isClientView && (
                      <>
                        <button
                          className={styles.reportAction}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(`/properties/${id}/reports/monthly_inspections/${report.id}/edit`, '_blank');
                          }}
                        >
                          Edit
                        </button>
                        <button
                          className={`${styles.reportAction} ${styles.reportActionRemove}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMonthlyInspectionReport(report.id);
                          }}
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                </div>
                    ))}
                  </div>
                ) : (
                  <p>There is no report.</p>
                )}
              </>
            );
          })()}

          {/* Weekly Inspection Reports */}
          {/* Laravel equivalent: CLIENT users only see published reports (status_id === 1) */}
          {property.isMetropolitanHousing && (() => {
            // Filter reports for CLIENT users - only show published (status_id === 1)
            const filteredWeeklyInspectionReports = (isClientUser || isClientView)
              ? (propertyReports.weeklyInspectionReports || []).filter(report => report.status_id === 1)
              : (propertyReports.weeklyInspectionReports || []);

            return (
              <>
                <div className={styles.reportSectionHeader}>
                  Weekly Inspection Reports
                </div>
                {filteredWeeklyInspectionReports.length > 0 ? (
                  <div className={styles.reportsGrid}>
                    {filteredWeeklyInspectionReports.map((report) => (
                    <div key={report.id} className={styles.reportCard}>
                      <div className={styles.reportLeft}>
                        <img src="/images/pdf.png" alt="PDF" />
                      </div>
                      <div className={styles.reportRight}>
                        <p className={styles.reportTitle}>
                          {report.weekNumber} ({report.year})
                        </p>
                        <p className={styles.reportShort}>
                          <span>Updated: </span>
                          {report.updated_at}
                        </p>
                      </div>
                      <div className={styles.reportActions}>
                        {/* Laravel equivalent: CLIENT users should NOT see Edit/Remove buttons */}
                        {!isClientUser && !isClientView && (
                          <>
                            <button
                              className={styles.reportAction}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`/properties/${id}/reports/monthly_inspections/${report.id}/edit?weekly=1`, '_blank');
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className={`${styles.reportAction} ${styles.reportActionRemove}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMonthlyInspectionReport(report.id);
                              }}
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p>There is no report.</p>
              )}
              </>
            );
          })()}

          {/* Initial Inspection Report */}
          {/* Laravel equivalent: CLIENT users only see published reports (status_id === 1) */}
          {(() => {
            // Filter reports for CLIENT users - only show published (status_id === 1)
            const filteredInitialInspectionReports = (isClientUser || isClientView)
              ? (propertyReports.initialInspectionReports || []).filter(report => report.status_id === 1)
              : (propertyReports.initialInspectionReports || []);

            // Hide entire section for client users if there are no reports
            if ((isClientUser || isClientView) && filteredInitialInspectionReports.length === 0) {
              return null;
            }

            return (
              <>
                <div className={styles.reportSectionHeader}>
                  Initial Inspection Report
                </div>
                {filteredInitialInspectionReports.length > 0 ? (
                  <div className={styles.reportsGrid}>
                    {filteredInitialInspectionReports.map((report) => (
                      <div
                        key={report.id}
                        className={styles.reportCard}
                        onClick={() => handleDownloadInitialInspectionPDF(report.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className={styles.reportLeft}>
                          <img src="/images/pdf.png" alt="PDF" />
                        </div>
                        <div className={styles.reportRight}>
                          <p className={styles.reportTitle}>{report.title}</p>
                          <p className={styles.reportShort}>
                            <span>Updated: </span>
                            {report.updated_at}
                          </p>
                        </div>
                        <div className={styles.reportActions}>
                          {/* Laravel equivalent: CLIENT users should NOT see Edit/Remove buttons */}
                          {!isClientUser && !isClientView && (
                            <>
                              <button
                                className={styles.reportAction}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(
                                    `/properties/${id}/reports/initial_inspections/${report.id}/edit`, '_blank'
                                  );
                                }}
                                style={{ marginRight: "5px" }}
                              >
                                Edit
                              </button>
                              <button
                                className={`${styles.reportAction} ${styles.reportActionRemove}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteInitialInspectionReport(report.id);
                                }}
                              >
                                Remove
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>There is no report.</p>
                )}
              </>
            );
          })()}

          {/* Property Proposal */}
          {/* Laravel equivalent: CLIENT users only see published (status_id === 1) or signed (status_id === 2) reports */}
          {(() => {
            // Filter reports for CLIENT users - only show published or signed
            const filteredPropertyProposals = (isClientUser || isClientView)
              ? (propertyReports.propertyProposals || []).filter(report => report.status_id === 1 || report.status_id === 2)
              : (propertyReports.propertyProposals || []);

            // Hide entire section for client users if there are no reports
            if ((isClientUser || isClientView) && filteredPropertyProposals.length === 0) {
              return null;
            }

            return (
              <>
                <div className={styles.reportSectionHeader}>Property Proposal</div>
                {filteredPropertyProposals.length > 0 ? (
                  <div className={styles.reportsGrid}>
                    {filteredPropertyProposals.map((report) => (
                      <React.Fragment key={report.id}>
                        {/* Unsigned Proposal Card */}
                        <div
                          className={styles.reportCard}
                          onClick={() => handleDownloadPropertyProposalPDF(report.id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className={styles.reportLeft}>
                            <img src="/images/pdf.png" alt="PDF" />
                          </div>
                          <div className={styles.reportRight}>
                            <p className={styles.reportTitle}>{report.title}</p>
                            <p className={styles.reportShort}>
                              <span>Updated: </span>
                              {report.updated_at}
                            </p>
                            {report.file_signed && (
                              <p className={styles.reportShort} style={{ color: '#5cb85c', fontWeight: 'bold' }}>
                                <span>Status: </span>
                                SIGNED
                              </p>
                            )}
                          </div>
                          <div className={styles.reportActions}>
                            {/* Laravel: _property_reports.blade.php:200-212 */}
                            {/* CLIENT users should NOT see Edit/Remove buttons */}
                            {!isClientUser && !isClientView && hasPermission('properties.reports.property_proposal') ? (
                              <>
                                {/* Edit and Remove buttons for users with property_proposal permission */}
                                <button
                                  className={styles.reportAction}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(`/properties/${id}/reports/property_proposals/${report.id}/edit`, '_blank');
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  className={`${styles.reportAction} ${styles.reportActionRemove}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePropertyProposal(report.id);
                                  }}
                                >
                                  Remove
                                </button>
                              </>
                            ) : (
                              <>
                                {/* Sign button only for users WITHOUT property_proposal permission, who have properties.show */}
                                {!report.file_signed && hasPermission('properties.show') && (
                                  <button
                                    className={styles.reportAction}
                                    style={{ backgroundColor: '#5cb85c', color: 'white' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      window.open(`/propertyproposals/${report.id}/sign`, '_blank');
                                    }}
                                  >
                                    Sign
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {/* Signed Proposal Card - Laravel: _property_reports.blade.php:217-239 */}
                        {report.file_signed && (
                          <div
                            className={styles.reportCard}
                            onClick={() => handleDownloadSignedPropertyProposal(report.id)}
                            style={{ cursor: 'pointer', backgroundColor: '#f0f9ff', borderColor: '#5cb85c' }}
                          >
                            <div className={styles.reportLeft}>
                              <img src="/images/pdf.png" alt="Signed PDF" />
                            </div>
                            <div className={styles.reportRight}>
                              <p className={styles.reportTitle}>
                                {report.title} (SIGNED)
                              </p>
                              <p className={styles.reportShort}>
                                <span>Signed by: </span>
                                {report.signed_client_contact_name || 'Unknown'}
                              </p>
                              <p className={styles.reportShort}>
                                <span>Date signed: </span>
                                {report.date_sign ? new Date(report.date_sign).toLocaleDateString('en-GB') : 'Unknown'}
                              </p>
                            </div>
                            <div className={styles.reportActions}>
                              <button
                                className={styles.reportAction}
                                style={{ backgroundColor: '#5cb85c', color: 'white' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadSignedPropertyProposal(report.id);
                                }}
                              >
                                Download Signed
                              </button>
                              {/* Laravel: _property_reports.blade.php:232-236 - Remove button for signed proposal */}
                              {/* CLIENT users should NOT see Remove button */}
                              {!isClientUser && !isClientView && hasPermission('properties.reports.property_proposal') && (
                                <button
                                  className={`${styles.reportAction} ${styles.reportActionRemove}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeletePropertyProposal(report.id);
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <p>There is no report.</p>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Global Guardians Services Section */}
      <div className={clientDashboardStyles.servicesSection}>
        <div className="panel">
          <div className="panel-title title-standard">
            <h3>Global Guardians services</h3>
          </div>
          <div className="panel-body">
            <div className={clientDashboardStyles.servicesGrid}>
              <a
                href="/client-knowledge/property-guardian-solution"
                className={`${clientDashboardStyles.serviceBox} services-link`}
                style={{ textDecoration: 'none' }}
              >
                <img
                  src="/images/services/property_guardian_solution_new.jpg"
                  alt="Property Guardian Solutions"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Property Guardian Solutions
                </div>
              </a>
              <a
                href="/client-knowledge/luxury-house-sitting"
                className={`${clientDashboardStyles.serviceBox} services-link`}
                style={{ textDecoration: 'none' }}
              >
                <img
                  src="/images/services/high_end_house_sitters_new.jpg"
                  alt="High End House Sitters"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  High End House Sitters
                </div>
              </a>
              <a
                href="/client-knowledge/safety-management"
                className={`${clientDashboardStyles.serviceBox} services-link`}
                style={{ textDecoration: 'none' }}
              >
                <img
                  src="/images/services/safety_management_new.jpg"
                  alt="Safety Management"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Safety
                  <br />
                  Management
                </div>
              </a>
              <a
                href="/client-knowledge/building-management"
                className={`${clientDashboardStyles.serviceBox} services-link`}
                style={{ textDecoration: 'none' }}
              >
                <img
                  src="/images/services/building_management_new.jpg"
                  alt="Building Management"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Building
                  <br />
                  Management
                </div>
              </a>
              <a
                href="/client-knowledge/improve-your-void"
                className={`${clientDashboardStyles.serviceBox} services-link`}
                style={{ textDecoration: 'none' }}
              >
                <img
                  src="/images/services/improve_your_voids_new.jpg"
                  alt="Improve Your Void"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Improve Your
                  <br />
                  Void
                </div>
              </a>
              <a
                href="/client-knowledge/life-enhancing-chances"
                className={`${clientDashboardStyles.serviceBox} services-link`}
                style={{ textDecoration: 'none' }}
              >
                <img
                  src="/images/services/life_enhancing_chances_new.jpg"
                  alt="Life Enhancing Chances"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Life Enhancing Chances
                </div>
              </a>
              <a
                href="/client-knowledge/property-guardian-insurance"
                className={`${clientDashboardStyles.serviceBox} services-link`}
                style={{ textDecoration: 'none' }}
              >
                <img
                  src="/images/services/property_guardian_insurance_new.jpg"
                  alt="Property Guardian Insurance"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Property Guardian Insurance
                </div>
              </a>
              <a
                href="/client-knowledge/void-into-profit"
                className={`${clientDashboardStyles.serviceBox} services-link`}
                style={{ textDecoration: 'none' }}
              >
                <img
                  src="/images/services/voids_into_profit_new.jpg"
                  alt="Void Into Profit"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Void Into
                  <br />
                  Profit
                </div>
              </a>
              <a
                href="/client-knowledge/proptech-software-solutions"
                className={`${clientDashboardStyles.serviceBox} services-link`}
                style={{ textDecoration: 'none' }}
              >
                <img
                  src="/images/services/empty_building_rates_reduction_new.jpg"
                  alt="Proptech Software Solutions"
                  className={clientDashboardStyles.serviceImage}
                />
                <div className={clientDashboardStyles.serviceText}>
                  Proptech Software Solutions
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Re-open staff-only conditional for Savings, Maintenance, and Actions sections */}
      {hasPermission('properties.all_info') && !isClientUser && !isClientView && (
        <>
          {/* Savings and VIP Section - Laravel: _savings_and_vip_next_to.blade.php */}
          <div className="col-xs-12">
            <div className="panel-title title-standard">
              <h3>Property savings & Voids Into Profit to date</h3>
            </div>
            <div className="panel-group">
              <div
                className={`row ${styles.savingsRow}`}
                style={{ justifyContent: client?.client_type === 'gs_solutions' ? 'center' : 'flex-start' }}
              >
                {/* Laravel: Only show VIP box for GS Solutions clients (line 9 in _savings_and_vip_next_to.blade.php) */}
                {client?.client_type !== 'gs_solutions' && (
                  <div className={`${styles.savingBox} ${styles.savingBoxRed}`}>
                    <img src="/images/peppa-red.png" alt="Savings" />
                    <p className={styles.cash}>+£{formatCurrency(savings?.savings_made)}</p>
                    <p className={styles.savingInformation}>
                      SAVINGS MADE BY USING GLOBAL GUARDIANS
                    </p>
                    {savings?.savings_data_incomplete && (
                      <p className={styles.savingInformation} style={{ color: '#ffe08a', marginTop: '6px' }}>
                        ⚠ Date Taken On missing — savings can't be calculated
                      </p>
                    )}
                  </div>
                )}
                {/* VIP box - always shown */}
                <div className={`${styles.savingBox} ${styles.savingBoxMint}`}>
                  <img src="/images/hand-mint.png" alt="VIP" />
                  <p className={styles.cash}>+£{formatCurrency(savings?.vip_amount)}</p>
                  <p className={styles.savingInformation}>
                    INCOME GENERATED THROUGH VOIDS INTO PROFIT
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Maintenance Section */}
          <div className="panel">
            <div className="panel-title title-standard">
              <h3>Maintenance</h3>
              <a href="/tickets/create" target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
                <i className="glyphicon glyphicon-plus"></i> Add new ticket
              </a>
            </div>
            <div className="panel-body">
              {maintenance.length > 0 ? (
                <div className="table-responsive">
                  {/* DataTable Controls */}
                  <div className={styles.tableToolbar}>
                    <div className={styles.tableToolbarEntries}>
                      <label>
                        Show
                        <select
                          className="form-control input-sm"
                          value={maintenancePageSize}
                          onChange={(e) => setMaintenancePageSize(parseInt(e.target.value))}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={30}>30</option>
                          <option value={50}>50</option>
                          <option value={75}>75</option>
                          <option value={100}>100</option>
                        </select>
                        entries
                      </label>
                    </div>
                    <div className={styles.tableToolbarSearch}>
                      <label>
                        Search:
                        <input
                          type="search"
                          className="form-control input-sm"
                          placeholder=""
                          value={maintenanceSearchTerm}
                          onChange={(e) => setMaintenanceSearchTerm(e.target.value)}
                        />
                      </label>
                    </div>
                    <div ref={maintenanceColumnVisibilityRef} style={{ position: "relative", display: "inline-block", marginLeft: "10px" }}>
                      <button
                        className="btn btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowMaintenanceColumnVisibility(!showMaintenanceColumnVisibility);
                        }}
                        style={{ minWidth: 120 }}
                      >
                        Column visibility
                      </button>
                      {showMaintenanceColumnVisibility && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            padding: '10px',
                            zIndex: 1000,
                            minWidth: '200px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            marginTop: '4px'
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>
                            Toggle Columns:
                          </h4>
                          {[
                            { key: 'id', label: 'ID' },
                            { key: 'status', label: 'Status' },
                            { key: 'urgency', label: 'Urgency' },
                            { key: 'propertyName', label: 'Property Name' },
                            { key: 'submitted', label: 'Submitted' },
                            { key: 'deadline', label: 'Deadline' },
                            { key: 'completed', label: 'Completed' },
                            { key: 'jobType', label: 'Job type' },
                            { key: 'contractorFirst', label: 'C. First' },
                            { key: 'contractorLast', label: 'C. Last' },
                            { key: 'contractor', label: 'Contractor' },
                            { key: 'costOfWorks', label: 'Cost of works' },
                            { key: 'actions', label: 'Actions' }
                          ].map((col) => (
                            <label
                              key={col.key}
                              onClick={(e) => {
                                e.stopPropagation();
                                setMaintenanceVisibleColumns(prev => ({
                                  ...prev,
                                  [col.key]: !prev[col.key]
                                }));
                              }}
                              style={{
                                display: 'block',
                                padding: '6px 8px',
                                cursor: 'pointer',
                                userSelect: 'none',
                                fontSize: '13px'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={maintenanceVisibleColumns[col.key]}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  setMaintenanceVisibleColumns(prev => ({
                                    ...prev,
                                    [col.key]: !prev[col.key]
                                  }));
                                }}
                                onClick={(e) => e.stopPropagation()}
                                style={{ marginRight: '8px' }}
                              />
                              <span>{col.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* DataTable */}
                  <table className="table table-striped table-bordered table-hover dt-responsive">
                    <thead>
                      <tr>
                        {maintenanceVisibleColumns.id && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            ID <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.status && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            Status <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.urgency && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            Urgency <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.propertyName && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            Property Name <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.submitted && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            Submitted <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.deadline && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            Deadline <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.completed && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            Completed <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.jobType && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            Job type <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.contractorFirst && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            C. First <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.contractorLast && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            C. Last <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.contractor && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            Contractor <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.costOfWorks && (
                          <th className="sorting" style={{ cursor: 'pointer' }}>
                            Cost of works <i className="fa fa-sort"></i>
                          </th>
                        )}
                        {maintenanceVisibleColumns.actions && (
                          <th>Actions</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredMaintenance.map((ticket) => (
                        <tr key={ticket.id}>
                          {maintenanceVisibleColumns.id && <td>{ticket.id}</td>}
                          {maintenanceVisibleColumns.status && (
                            <td>
                              <span
                                className="label"
                                style={{
                                  backgroundColor: getStatusColor(ticket.status, ticket.statusId || ticket.status_id),
                                  color: '#fff',
                                  padding: '2px 8px',
                                  borderRadius: '3px',
                                  fontSize: '11px',
                                  fontWeight: 'bold'
                                }}
                              >
                                {ticket.status}
                              </span>
                            </td>
                          )}
                          {maintenanceVisibleColumns.urgency && (
                            <td>
                              <span
                                className="label"
                                style={{
                                  backgroundColor: getUrgencyColor(ticket.urgency),
                                  color: '#fff',
                                  padding: '2px 8px',
                                  borderRadius: '3px',
                                  fontSize: '11px',
                                  fontWeight: 'bold'
                                }}
                              >
                                {ticket.urgency}
                              </span>
                            </td>
                          )}
                          {maintenanceVisibleColumns.propertyName && <td>{ticket.propertyName}</td>}
                          {maintenanceVisibleColumns.submitted && (
                            <td>{formatDateLaravel(ticket.submitted || ticket.created_at)}</td>
                          )}
                          {maintenanceVisibleColumns.deadline && (
                            <td>{formatDateLaravel(ticket.deadline || ticket.date_estimated_completion)}</td>
                          )}
                          {maintenanceVisibleColumns.completed && (
                            <td>{formatDateLaravel(ticket.completed || ticket.date_completed) || 'Not specified'}</td>
                          )}
                          {maintenanceVisibleColumns.jobType && <td>{ticket.jobType}</td>}
                          {maintenanceVisibleColumns.contractorFirst && <td>{ticket.contractorFirstName}</td>}
                          {maintenanceVisibleColumns.contractorLast && <td>{ticket.contractorLastName}</td>}
                          {maintenanceVisibleColumns.contractor && (
                            <td>
                              {ticket.contractor && ticket.contractor !== 'Not assigned' && ticket.contractor_id ? (
                                <a href={`/contractors/${ticket.contractor_id}`} target="_blank" rel="noopener noreferrer">
                                  {ticket.contractor}
                                </a>
                              ) : (
                                ticket.contractor || '-'
                              )}
                            </td>
                          )}
                          {maintenanceVisibleColumns.costOfWorks && (
                            <td>
                              {ticket.cost_of_works ? `£${parseFloat(ticket.cost_of_works).toFixed(2)}` : '-'}
                            </td>
                          )}
                          {maintenanceVisibleColumns.actions && (
                            <td>
                              <a href={`/tickets/${ticket.id}`} target="_blank" rel="noopener noreferrer" className="btn btn-xs btn-info">
                                Show
                              </a>
                              <a href={`/tickets/${ticket.id}/edit`} target="_blank" rel="noopener noreferrer" className="btn btn-xs btn-primary">
                                Edit
                              </a>
                              <button
                                className="btn btn-xs btn-danger"
                                onClick={() => handleDeleteTicket(ticket.id)}
                              >
                                Delete
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="row">
                    <div className="col-sm-5">
                      <div className="dataTables_info">
                        Showing {totalMaintenanceCount === 0 ? 0 : ((maintenanceCurrentPage - 1) * maintenancePageSize) + 1} to {Math.min(maintenanceCurrentPage * maintenancePageSize, totalMaintenanceCount)} of {totalMaintenanceCount} entries
                      </div>
                    </div>
                    <div className="col-sm-7">
                      <div className="dataTables_paginate paging_simple_numbers">
                        <ul className="pagination">
                          <li className={`paginate_button previous ${maintenanceCurrentPage === 1 ? 'disabled' : ''}`}>
                            <a href="#" onClick={(e) => { e.preventDefault(); if (maintenanceCurrentPage > 1) setMaintenanceCurrentPage(maintenanceCurrentPage - 1); }}>
                              Previous
                            </a>
                          </li>
                          {/* Limit page buttons to avoid rendering too many - show first 5, current area, and last 5 */}
                          {totalMaintenancePages <= 10 ? (
                            Array.from({ length: totalMaintenancePages }, (_, i) => i + 1).map(page => (
                              <li key={page} className={`paginate_button ${page === maintenanceCurrentPage ? 'active' : ''}`}>
                                <a href="#" onClick={(e) => { e.preventDefault(); setMaintenanceCurrentPage(page); }}>
                                  {page}
                                </a>
                              </li>
                            ))
                          ) : (
                            <>
                              {/* First page */}
                              <li className={`paginate_button ${1 === maintenanceCurrentPage ? 'active' : ''}`}>
                                <a href="#" onClick={(e) => { e.preventDefault(); setMaintenanceCurrentPage(1); }}>1</a>
                              </li>
                              {/* Ellipsis if needed */}
                              {maintenanceCurrentPage > 4 && <li className="paginate_button disabled"><span>...</span></li>}
                              {/* Pages around current */}
                              {Array.from({ length: 5 }, (_, i) => maintenanceCurrentPage - 2 + i)
                                .filter(page => page > 1 && page < totalMaintenancePages)
                                .map(page => (
                                  <li key={page} className={`paginate_button ${page === maintenanceCurrentPage ? 'active' : ''}`}>
                                    <a href="#" onClick={(e) => { e.preventDefault(); setMaintenanceCurrentPage(page); }}>
                                      {page}
                                    </a>
                                  </li>
                                ))
                              }
                              {/* Ellipsis if needed */}
                              {maintenanceCurrentPage < totalMaintenancePages - 3 && <li className="paginate_button disabled"><span>...</span></li>}
                              {/* Last page */}
                              <li className={`paginate_button ${totalMaintenancePages === maintenanceCurrentPage ? 'active' : ''}`}>
                                <a href="#" onClick={(e) => { e.preventDefault(); setMaintenanceCurrentPage(totalMaintenancePages); }}>{totalMaintenancePages}</a>
                              </li>
                            </>
                          )}
                          <li className={`paginate_button next ${maintenanceCurrentPage === totalMaintenancePages || totalMaintenancePages === 0 ? 'disabled' : ''}`}>
                            <a href="#" onClick={(e) => { e.preventDefault(); if (maintenanceCurrentPage < totalMaintenancePages) setMaintenanceCurrentPage(maintenanceCurrentPage + 1); }}>
                              Next
                            </a>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p>No maintenance records available.</p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Actions Section */}
      {!isClientUser && !isClientView && (
        <div className="panel">
          <div className="panel-title title-standard">
            <h3>Actions</h3>
          </div>
          <div className="panel-body">
            {hasPermission('properties.all_info') && (
              <>
                <a
                  className="btn-head"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExportPropertyDetails();
                  }}
                >
                  Export (CSV) property details
                  <i className="glyphicon glyphicon-triangle-right"></i>
                </a>
                <br />
                <a
                  className="btn-head"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleExportGuardiansDetails();
                  }}
                >
                  Export (CSV) placed guardians details
                  <i className="glyphicon glyphicon-triangle-right"></i>
                </a>
                <br />
              </>
            )}

            {/* PDF export button - always visible (no permission check in Laravel) */}
            <a
              className="btn-head"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handleExportGuardiansPDF();
              }}
            >
              Export (PDF) placed guardians details
              <i className="glyphicon glyphicon-triangle-right"></i>
            </a>
          </div>
        </div>
      )}

      {/* Global Guardians Services Section - CLIENT ONLY (Laravel: show_for_client.blade.php lines 65-76) */}
      {/* {(isClientUser || isClientView) && (
        <div className="row">
          <div className="col-md-12">
            <div className="panel-title title-standard">
              <h3>Global Guardians services</h3>
            </div>
            <div className="panel-group">
              <div className="row">
                <div className="col-md-4">
                  <a href="/client-knowledge/property-guardian-solution" className="services-link" style={{ textDecoration: 'none' }}>
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px', cursor: 'pointer', transition: 'transform 0.2s', ':hover': { transform: 'scale(1.02)' } }}>
                      <div style={{ width: '100%', height: '200px', overflow: 'hidden' }}>
                        <img src="/assets/images/services/property_guardian_solution_new.jpg" alt="Property Guardian Solutions" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                        Property Guardian Solutions
                      </div>
                    </div>
                  </a>
                </div>

                <div className="col-md-4">
                  <a href="/client-knowledge/luxury-house-sitting" className="services-link" style={{ textDecoration: 'none' }}>
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px', cursor: 'pointer', transition: 'transform 0.2s' }}>
                      <div style={{ width: '100%', height: '200px', overflow: 'hidden' }}>
                        <img src="/assets/images/services/high_end_house_sitters_new.jpg" alt="High End House Sitters" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                        High End House Sitters
                      </div>
                    </div>
                  </a>
                </div>

                <div className="col-md-4">
                  <a href="/client-knowledge/safety-management" className="services-link" style={{ textDecoration: 'none' }}>
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px', cursor: 'pointer', transition: 'transform 0.2s' }}>
                      <div style={{ width: '100%', height: '200px', overflow: 'hidden' }}>
                        <img src="/assets/images/services/safety_management_new.jpg" alt="Safety Management" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                        Safety<br />Management
                      </div>
                    </div>
                  </a>
                </div>

                <div className="col-md-4">
                  <a href="/client-knowledge/building-management" className="services-link" style={{ textDecoration: 'none' }}>
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px', cursor: 'pointer', transition: 'transform 0.2s' }}>
                      <div style={{ width: '100%', height: '200px', overflow: 'hidden' }}>
                        <img src="/assets/images/services/building_management_new.jpg" alt="Building Management" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                        Building<br />Management
                      </div>
                    </div>
                  </a>
                </div>

                <div className="col-md-4">
                  <a href="/client-knowledge/improve-your-void" className="services-link" style={{ textDecoration: 'none' }}>
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px', cursor: 'pointer', transition: 'transform 0.2s' }}>
                      <div style={{ width: '100%', height: '200px', overflow: 'hidden' }}>
                        <img src="/assets/images/services/improve_your_voids_new.jpg" alt="Improve Your Void" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                        Improve Your<br />Void
                      </div>
                    </div>
                  </a>
                </div>

                <div className="col-md-4">
                  <a href="/client-knowledge/life-enhancing-chances" className="services-link" style={{ textDecoration: 'none' }}>
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px', cursor: 'pointer', transition: 'transform 0.2s' }}>
                      <div style={{ width: '100%', height: '200px', overflow: 'hidden' }}>
                        <img src="/assets/images/services/life_enhancing_chances_new.jpg" alt="Life Enhancing Chances" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                        Life Enhancing Chances
                      </div>
                    </div>
                  </a>
                </div>

                <div className="col-md-4">
                  <a href="/client-knowledge/property-guardian-insurance" className="services-link" style={{ textDecoration: 'none' }}>
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px', cursor: 'pointer', transition: 'transform 0.2s' }}>
                      <div style={{ width: '100%', height: '200px', overflow: 'hidden' }}>
                        <img src="/assets/images/services/property_guardian_insurance_new.jpg" alt="Property Guardian Insurance" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                        Property Guardian Insurance
                      </div>
                    </div>
                  </a>
                </div>

                <div className="col-md-4">
                  <a href="/client-knowledge/void-into-profit" className="services-link" style={{ textDecoration: 'none' }}>
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px', cursor: 'pointer', transition: 'transform 0.2s' }}>
                      <div style={{ width: '100%', height: '200px', overflow: 'hidden' }}>
                        <img src="/assets/images/services/voids_into_profit_new.jpg" alt="Void Into Profit" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                        Void Into<br />Profit
                      </div>
                    </div>
                  </a>
                </div>

                <div className="col-md-4">
                  <a href="/client-knowledge/proptech-software-solutions" className="services-link" style={{ textDecoration: 'none' }}>
                    <div style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px', cursor: 'pointer', transition: 'transform 0.2s' }}>
                      <div style={{ width: '100%', height: '200px', overflow: 'hidden' }}>
                        <img src="/assets/images/services/empty_building_rates_reduction_new.jpg" alt="Proptech Software Solutions" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ padding: '15px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>
                        Proptech Software Solutions
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* Add Room Modal */}
      <AddRoomModal
        isOpen={isAddRoomModalOpen}
        onClose={() => setIsAddRoomModalOpen(false)}
        onRoomAdded={async (newRoom) => {
          try {
            // Refresh rooms from API to get the latest data
            const roomsResponse = await roomsService.getRoomsByPropertyId(id);
            if (roomsResponse.success) {
              setRooms(roomsResponse.data);
              toast.success("Room added successfully!");
            } else {
              toast.error("Room added but failed to refresh list");
            }
            setIsAddRoomModalOpen(false);
          } catch (error) {
            console.error("Error refreshing rooms after adding:", error);
            toast.success("Room added successfully!");
            setIsAddRoomModalOpen(false);
          }
        }}
        propertyId={id}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        onNoteAdded={async (newNote) => {
          try {
            // Refresh notes from API to get the latest data
            const notesResponse = await notesService.getPropertyNotes(id);
            if (notesResponse.success) {
              setNotes(notesResponse.data);
              toast.success("Note added successfully!");
            } else {
              toast.error("Note added but failed to refresh list");
            }
            setIsAddNoteModalOpen(false);
          } catch (error) {
            console.error("Error refreshing notes after adding:", error);
            toast.success("Note added successfully!");
            setIsAddNoteModalOpen(false);
          }
        }}
        propertyId={id}
      />

      {/* Edit Note Modal */}
      <EditNoteModal
        isOpen={isEditNoteModalOpen}
        onClose={() => {
          setIsEditNoteModalOpen(false);
          setEditingNote(null);
        }}
        note={editingNote}
        onNoteUpdated={handleNoteUpdated}
      />

      {/* Assign Guardian Modal */}
      {console.log('🔧 [DEBUG] Rendering AssignGuardianModal with props:', {
        isOpen: isAssignGuardianModalOpen,
        propertyId: property?.id,
        roomId: selectedRoomForAssignment?.id,
        propertyName: property?.name,
        roomName: selectedRoomForAssignment?.name
      })}
      <AssignGuardianModal
        isOpen={isAssignGuardianModalOpen}
        onClose={handleCloseAssignGuardianModal}
        propertyId={property?.id}
        roomId={selectedRoomForAssignment?.id}
        propertyName={property?.name}
        roomName={selectedRoomForAssignment?.name}
        onAssignSuccess={handleAssignmentSuccess}
      />

      <DocumentPreviewModal
        isOpen={isDocumentPreviewModalOpen}
        onClose={() => setIsDocumentPreviewModalOpen(false)}
        propertyId={property?.id}
        documentId={previewDocument?.id}
        documentName={previewDocument?.name || previewDocument?.name_or_type}
        documentType={previewDocument?.type || 'certificate'}
      />

      {/* Manage Rooms Modal */}
      <ManageMetersModal
        isOpen={isAddMeterModalOpen}
        onClose={() => setIsAddMeterModalOpen(false)}
        propertyId={id}
        propertyName={property?.name}
        onMetersChange={refreshMeters}
        addOnly
      />

      <ManageMetersModal
        isOpen={isManageMetersModalOpen}
        onClose={() => setIsManageMetersModalOpen(false)}
        propertyId={id}
        propertyName={property?.name}
        onMetersChange={refreshMeters}
      />

      <ManageRoomsModal
        isOpen={isManageRoomsModalOpen}
        onClose={() => setIsManageRoomsModalOpen(false)}
        propertyId={property?.id}
        propertyName={property?.name}
        onRoomsChange={refreshRooms}
      />
    </div>
  );
};

export default PropertyShow;
